import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { 
  ArrowLeft, Search, FileText, Plus, User, X, 
  Eye, Mail, Printer, Clock, Building, Stethoscope, Trash2, Upload, Image as ImageIcon,
  MessageCircle, Send, Edit, MessageSquare, Phone, Check, Gift, Heart, MapPin, Users, Download, UserPlus, Settings, AlertCircle, BarChart3
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AIReferralButton } from '../components/referrals/AIReferralDialog';

// Inbound referral source types
const REFERRAL_SOURCE_TYPES = [
  "Dentist",
  "Patient",
  "Friend/Family",
  "Insurance",
  "Online/Website",
  "Other"
];

const ReferralManagementPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { practice } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('dentalToken');

  // Main Tab state - "to" (outbound) or "from" (inbound)
  const [mainTab, setMainTab] = useState('to');

  // Main state (Referral To)
  const [referrals, setReferrals] = useState([]);
  const [filteredReferrals, setFilteredReferrals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('list'); // 'list' or 'create'
  const [createdReferralId, setCreatedReferralId] = useState(null);
  
  // Create referral states
  const [step, setStep] = useState(1);
  const [patients, setPatients] = useState([]);
  const [dentists, setDentists] = useState([]);
  const [selectedDentist, setSelectedDentist] = useState(null);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [referralSources, setReferralSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  
  // Modals
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [showAddDentistModal, setShowAddDentistModal] = useState(false);
  const [showAddSourceModal, setShowAddSourceModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [referralToDelete, setReferralToDelete] = useState(null);
  
  // Email recipient selection modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalReferralId, setEmailModalReferralId] = useState(null);
  const [sendToSpecialist, setSendToSpecialist] = useState(true);
  const [sendToPatient, setSendToPatient] = useState(false);
  const [emailingReferral, setEmailingReferral] = useState(false);
  
  // SMS state
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsReferral, setSmsReferral] = useState(null);
  const [smsRecipient, setSmsRecipient] = useState('patient'); // 'patient', 'dentist', 'both'
  const [sendingSMS, setSendingSMS] = useState(false);
  
  // Ask DAN state
  const [showAskDan, setShowAskDan] = useState(false);
  const [danMessages, setDanMessages] = useState([]);
  const [danInput, setDanInput] = useState('');
  const [danLoading, setDanLoading] = useState(false);

  // PDF blob cache (avoids regenerating on view->print->download)
  const pdfCacheRef = useRef({});

  // ========== INBOUND REFERRALS STATE (Referral From) ==========
  const [inboundSources, setInboundSources] = useState([]);
  const [filteredInboundSources, setFilteredInboundSources] = useState([]);
  const [inboundSearchQuery, setInboundSearchQuery] = useState('');
  const [inboundLoading, setInboundLoading] = useState(false);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('all');
  
  // Inbound modals
  const [showAddInboundSourceModal, setShowAddInboundSourceModal] = useState(false);
  const [showEditInboundSourceModal, setShowEditInboundSourceModal] = useState(false);
  const [showViewInboundSourceModal, setShowViewInboundSourceModal] = useState(false);
  const [showDeleteInboundConfirm, setShowDeleteInboundConfirm] = useState(false);
  const [showLinkPatientModal, setShowLinkPatientModal] = useState(false);
  const [showThankYouTemplateModal, setShowThankYouTemplateModal] = useState(false);
  const [showSendThankYouModal, setShowSendThankYouModal] = useState(false);
  
  // Selected inbound source
  const [selectedInboundSource, setSelectedInboundSource] = useState(null);
  const [inboundSourceDetails, setInboundSourceDetails] = useState(null);
  
  // Add Referral modal state - supports both new and existing patients
  const [addReferralMode, setAddReferralMode] = useState('new'); // 'new' or 'existing'
  const [newReferralPatient, setNewReferralPatient] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    notes: ''
  });
  const [addReferralSendText, setAddReferralSendText] = useState(false);
  const [addingReferral, setAddingReferral] = useState(false);
  const [duplicatePatient, setDuplicatePatient] = useState(null);
  
  // Link patient state (for existing patients)
  const [linkPatientSourceId, setLinkPatientSourceId] = useState(null);
  const [linkPatientId, setLinkPatientId] = useState('');
  const [linkPatientNotes, setLinkPatientNotes] = useState('');
  const [linkSendThankYou, setLinkSendThankYou] = useState(true);
  const [linkSendEmail, setLinkSendEmail] = useState(true);
  const [linkSendSms, setLinkSendSms] = useState(true);
  const [linkingPatient, setLinkingPatient] = useState(false);
  
  // Thank you modal state
  const [thankYouSourceId, setThankYouSourceId] = useState(null);
  const [thankYouPatientId, setThankYouPatientId] = useState(null);
  const [thankYouSendEmail, setThankYouSendEmail] = useState(true);
  const [thankYouSendSms, setThankYouSendSms] = useState(true);
  const [thankYouCustomMessage, setThankYouCustomMessage] = useState('');
  
  // Template editing state
  const [thankYouTemplates, setThankYouTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Inbound form data
  const [inboundFormData, setInboundFormData] = useState({
    name: '',
    sourceType: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });
  const [savingInboundSource, setSavingInboundSource] = useState(false);
  const [sendingThankYou, setSendingThankYou] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    chiefConcern: '',
    requestedService: '',
    toothArea: '',
    urgency: 'routine',
    medicalAlerts: '',
    notesToSpecialist: '',
    images: []
  });
  const [uploadingImages, setUploadingImages] = useState(false);

  const [newPatient, setNewPatient] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: ''
  });

  const [newSource, setNewSource] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    specialty: ''
  });

  const [newDentist, setNewDentist] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'practice_staff'
  });

  useEffect(() => {
    loadReferrals();
    loadPatients();
    loadSpecialties();
    loadDentists();
    loadReferralSources();
    
    // Clean up any leftover print-only elements from other pages (e.g., Med Cards)
    const cleanupPrintElements = () => {
      const printElements = document.querySelectorAll('.print-only');
      printElements.forEach(el => el.remove());
    };
    cleanupPrintElements();
  }, []);

  // Handle URL parameters for direct edit from Patient Records
  useEffect(() => {
    const referralId = searchParams.get('referralId');
    const mode = searchParams.get('mode');
    
    if (referralId && mode === 'edit' && referrals.length > 0) {
      // Open the referral in edit mode
      handleEditReferral(referralId);
      // Clear URL parameters to avoid re-triggering
      navigate('/referral-management', { replace: true });
    }
  }, [searchParams, referrals]);

  // Load referral sources when specialty changes
  useEffect(() => {
    if (selectedSpecialty) {
      console.log('Specialty changed to:', selectedSpecialty);
      loadReferralSources(selectedSpecialty);
    }
  }, [selectedSpecialty]);

  useEffect(() => {
    const searchLower = searchQuery.toLowerCase().trim();
    
    if (!searchLower) {
      setFilteredReferrals(referrals);
      return;
    }
    
    const filtered = referrals.filter(referral => {
      const patientName = (referral.patientName || '').toLowerCase();
      const specialty = (referral.specialty || '').toLowerCase();
      const concern = (referral.chiefConcern || '').toLowerCase();
      const sourceName = (referral.referralSourceName || '').toLowerCase();
      
      return patientName.includes(searchLower) || 
             specialty.includes(searchLower) || 
             concern.includes(searchLower) ||
             sourceName.includes(searchLower);
    });
    
    setFilteredReferrals(filtered);
  }, [searchQuery, referrals]);

  useEffect(() => {
    if (!patientSearchTerm) {
      setFilteredPatients(patients);
    } else {
      const filtered = patients.filter(patient => 
        patient.firstName?.toLowerCase().includes(patientSearchTerm.toLowerCase()) ||
        patient.lastName?.toLowerCase().includes(patientSearchTerm.toLowerCase()) ||
        patient.email?.toLowerCase().includes(patientSearchTerm.toLowerCase()) ||
        `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(patientSearchTerm.toLowerCase())
      );
      setFilteredPatients(filtered);
    }
  }, [patientSearchTerm, patients]);

  // ========== INBOUND REFERRALS EFFECTS ==========
  
  // Load inbound sources when switching to "from" tab
  useEffect(() => {
    if (mainTab === 'from') {
      loadInboundSources();
    }
  }, [mainTab]);

  // Filter inbound sources based on search and type filter
  useEffect(() => {
    let filtered = inboundSources;
    
    // Apply type filter
    if (selectedTypeFilter !== 'all') {
      filtered = filtered.filter(s => s.sourceType === selectedTypeFilter);
    }
    
    // Apply search filter
    if (inboundSearchQuery.trim()) {
      const query = inboundSearchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        s.name?.toLowerCase().includes(query) ||
        s.email?.toLowerCase().includes(query) ||
        s.phone?.includes(query)
      );
    }
    
    setFilteredInboundSources(filtered);
  }, [inboundSources, inboundSearchQuery, selectedTypeFilter]);

  // ========== INBOUND REFERRALS FUNCTIONS ==========
  
  const loadInboundSources = async () => {
    try {
      setInboundLoading(true);
      const response = await fetch(`${backendUrl}/api/inbound-referrals/sources`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setInboundSources(data.sources || []);
      setFilteredInboundSources(data.sources || []);
    } catch (error) {
      console.error('Load inbound sources error:', error);
      toast({ title: "Error", description: "Failed to load referral sources", variant: "destructive" });
    } finally {
      setInboundLoading(false);
    }
  };

  const loadInboundSourceDetails = async (sourceId) => {
    try {
      const response = await fetch(`${backendUrl}/api/inbound-referrals/sources/${sourceId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setInboundSourceDetails(data);
    } catch (error) {
      console.error('Load source details error:', error);
      toast({ title: "Error", description: "Failed to load referral source details", variant: "destructive" });
    }
  };

  const handleAddInboundSource = async () => {
    if (!inboundFormData.sourceType) {
      toast({ title: "Error", description: "Please select a referral type", variant: "destructive" });
      return;
    }
    
    // Auto-populate name for Online/Website and Other types if left empty
    let finalFormData = { ...inboundFormData };
    if (!finalFormData.name.trim()) {
      if (finalFormData.sourceType === 'Online/Website') {
        finalFormData.name = 'Online';
      } else if (finalFormData.sourceType === 'Other') {
        finalFormData.name = 'Other';
      } else {
        toast({ title: "Error", description: "Name is required", variant: "destructive" });
        return;
      }
    }

    try {
      setSavingInboundSource(true);
      const response = await fetch(`${backendUrl}/api/inbound-referrals/sources`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(finalFormData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add referral source');
      }

      toast({ title: "Success", description: "Referral source added successfully" });
      setShowAddInboundSourceModal(false);
      resetInboundForm();
      await loadInboundSources();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSavingInboundSource(false);
    }
  };

  const handleUpdateInboundSource = async () => {
    if (!inboundFormData.name.trim()) {
      toast({ title: "Error", description: "Name is required", variant: "destructive" });
      return;
    }

    try {
      setSavingInboundSource(true);
      const response = await fetch(`${backendUrl}/api/inbound-referrals/sources/${selectedInboundSource.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(inboundFormData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update referral source');
      }

      toast({ title: "Success", description: "Referral source updated successfully" });
      setShowEditInboundSourceModal(false);
      resetInboundForm();
      await loadInboundSources();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSavingInboundSource(false);
    }
  };

  const handleDeleteInboundSource = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/inbound-referrals/sources/${selectedInboundSource.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete referral source');
      }

      toast({ title: "Success", description: "Referral source deleted successfully" });
      setShowDeleteInboundConfirm(false);
      setShowViewInboundSourceModal(false);
      setSelectedInboundSource(null);
      await loadInboundSources();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleSendThankYou = async (patientId) => {
    if (!selectedInboundSource || !patientId) return;

    try {
      setSendingThankYou(true);
      const response = await fetch(`${backendUrl}/api/inbound-referrals/send-thank-you/${selectedInboundSource.id}?patient_id=${patientId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send thank you');
      }

      const results = data.results;
      let message = "Thank you sent!";
      if (results.email.sent && results.sms.sent) {
        message = "Thank you sent via email and text!";
      } else if (results.email.sent) {
        message = "Thank you sent via email!";
      } else if (results.sms.sent) {
        message = "Thank you sent via text!";
      }

      toast({ title: "Success", description: message });
      // Refresh source details
      await loadInboundSourceDetails(selectedInboundSource.id);
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSendingThankYou(false);
    }
  };

  const openViewInboundSourceModal = async (source) => {
    setSelectedInboundSource(source);
    await loadInboundSourceDetails(source.id);
    setShowViewInboundSourceModal(true);
  };

  const openEditInboundSourceModal = (source) => {
    setSelectedInboundSource(source);
    setInboundFormData({
      name: source.name || '',
      sourceType: source.sourceType || '',
      email: source.email || '',
      phone: source.phone || '',
      address: source.address || '',
      notes: source.notes || ''
    });
    setShowEditInboundSourceModal(true);
  };

  const openDeleteInboundConfirm = (source) => {
    setSelectedInboundSource(source);
    setShowDeleteInboundConfirm(true);
  };

  const resetInboundForm = () => {
    setInboundFormData({
      name: '',
      sourceType: '',
      email: '',
      phone: '',
      address: '',
      notes: ''
    });
    setSelectedInboundSource(null);
  };

  const getInboundTypeColor = (type) => {
    const colors = {
      'Dentist': 'bg-blue-100 text-blue-800',
      'Patient': 'bg-green-100 text-green-800',
      'Friend/Family': 'bg-pink-100 text-pink-800',
      'Insurance': 'bg-purple-100 text-purple-800',
      'Online/Website': 'bg-orange-100 text-orange-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getInboundTypeIcon = (type) => {
    switch (type) {
      case 'Dentist': return <User className="h-4 w-4" />;
      case 'Patient': return <Users className="h-4 w-4" />;
      case 'Friend/Family': return <Heart className="h-4 w-4" />;
      default: return <User className="h-4 w-4" />;
    }
  };

  // ========== NEW INBOUND REFERRAL FUNCTIONS ==========

  const openLinkPatientModal = (source) => {
    setLinkPatientSourceId(source.id);
    setSelectedInboundSource(source);
    // Reset new patient mode (default)
    setAddReferralMode('new');
    setNewReferralPatient({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      notes: ''
    });
    setAddReferralSendText(false);
    setDuplicatePatient(null);
    // Reset existing patient mode
    setLinkPatientId('');
    setLinkPatientNotes('');
    setLinkSendThankYou(true);
    setLinkSendEmail(true);
    setLinkSendSms(true);
    setShowLinkPatientModal(true);
  };

  // Handle adding NEW patient from referral
  const handleAddNewPatientReferral = async () => {
    if (!newReferralPatient.firstName.trim() || !newReferralPatient.lastName.trim()) {
      toast({ title: "Error", description: "First name and last name are required", variant: "destructive" });
      return;
    }

    try {
      setAddingReferral(true);
      setDuplicatePatient(null);
      
      const response = await fetch(`${backendUrl}/api/inbound-referrals/add-patient-referral`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sourceId: linkPatientSourceId,
          firstName: newReferralPatient.firstName.trim(),
          lastName: newReferralPatient.lastName.trim(),
          email: newReferralPatient.email.trim() || null,
          phone: newReferralPatient.phone.trim() || null,
          dateOfBirth: newReferralPatient.dateOfBirth || null,
          notes: newReferralPatient.notes.trim() || null,
          sendText: addReferralSendText
        })
      });

      const data = await response.json();

      if (data.isDuplicate) {
        // Show duplicate warning and offer to link existing patient
        setDuplicatePatient(data.existingPatient);
        toast({ 
          title: "Duplicate Found", 
          description: data.message + ". Would you like to link this existing patient instead?", 
          variant: "warning"
        });
        setAddingReferral(false);
        return;
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to add patient');
      }

      // Build success message
      let successMessage = `${newReferralPatient.firstName} ${newReferralPatient.lastName} has been added`;
      if (data.autoEmailSent) {
        const tierLabel = data.thankYouResults?.email?.tier || '';
        successMessage += ` and a thank you email was sent to ${selectedInboundSource?.name}`;
        if (tierLabel) successMessage += ` (${tierLabel})`;
      }
      if (data.thankYouResults?.sms?.sent) {
        successMessage += ` (text also sent)`;
      }

      toast({ title: "Success!", description: successMessage });
      setShowLinkPatientModal(false);
      await loadInboundSources();
      await loadPatients(); // Refresh patients list
    } catch (error) {
      console.error('Add patient referral error:', error);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setAddingReferral(false);
    }
  };

  // Link duplicate patient to source
  const handleLinkDuplicatePatient = () => {
    if (duplicatePatient) {
      setAddReferralMode('existing');
      setLinkPatientId(duplicatePatient.id);
      setDuplicatePatient(null);
    }
  };

  const handleLinkPatient = async () => {
    if (!linkPatientId || !linkPatientSourceId) {
      toast({ title: "Error", description: "Please select a patient", variant: "destructive" });
      return;
    }

    try {
      setLinkingPatient(true);
      const response = await fetch(`${backendUrl}/api/inbound-referrals/link-patient`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patientId: linkPatientId,
          sourceId: linkPatientSourceId,
          notes: linkPatientNotes,
          sendThankYou: linkSendThankYou,
          sendEmail: linkSendEmail,
          sendSms: linkSendSms
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to link patient');
      }

      let successMsg = "Patient linked to referral source successfully!";
      if (linkSendThankYou) {
        const results = data.thankYouResults;
        if (results?.email?.sent && results?.sms?.sent) {
          successMsg += " Thank you sent via email and text!";
        } else if (results?.email?.sent) {
          successMsg += " Thank you sent via email!";
        } else if (results?.sms?.sent) {
          successMsg += " Thank you sent via text!";
        }
      }

      toast({ title: "Success", description: successMsg });
      setShowLinkPatientModal(false);
      await loadInboundSources();
      
      // Refresh source details if viewing
      if (selectedInboundSource) {
        await loadInboundSourceDetails(selectedInboundSource.id);
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLinkingPatient(false);
    }
  };

  const handleDownloadThankYouPdf = async (sourceId, patientId) => {
    try {
      toast({ title: "Generating PDF", description: "Creating thank you letter..." });

      const cacheKey = `ty_${sourceId}_${patientId}`;
      let blob = pdfCacheRef.current[cacheKey];
      if (!blob) {
        const response = await fetch(`${backendUrl}/api/inbound-referrals/generate-thank-you-pdf/${sourceId}?patient_id=${patientId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          throw new Error('Failed to generate PDF');
        }

        blob = await response.blob();
        pdfCacheRef.current[cacheKey] = blob;
        setTimeout(() => { delete pdfCacheRef.current[cacheKey]; }, 300000);
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `thank_you_letter.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({ title: "Success", description: "Thank you letter downloaded!" });
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handlePrintThankYouPdf = async (sourceId, patientId) => {
    try {
      toast({ title: "Preparing Print", description: "Loading thank you letter..." });

      const cacheKey = `ty_${sourceId}_${patientId}`;
      let blob = pdfCacheRef.current[cacheKey];
      if (!blob) {
        const response = await fetch(`${backendUrl}/api/inbound-referrals/generate-thank-you-pdf/${sourceId}?patient_id=${patientId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          throw new Error('Failed to generate PDF');
        }

        blob = await response.blob();
        pdfCacheRef.current[cacheKey] = blob;
        setTimeout(() => { delete pdfCacheRef.current[cacheKey]; }, 300000);
      }

      const url = URL.createObjectURL(blob);
      
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = url;
      
      document.body.appendChild(iframe);
      
      iframe.onload = function() {
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            
            setTimeout(() => {
              document.body.removeChild(iframe);
              URL.revokeObjectURL(url);
            }, 1000);
          } catch (e) {
            const link = document.createElement('a');
            link.href = url;
            link.download = `thank_you_letter.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            document.body.removeChild(iframe);
            URL.revokeObjectURL(url);
            toast({ title: "Note", description: "PDF downloaded instead. Open to print." });
          }
        }, 500);
      };
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const openSendThankYouModal = (sourceId, patientId) => {
    setThankYouSourceId(sourceId);
    setThankYouPatientId(patientId);
    setThankYouSendEmail(true);
    setThankYouSendSms(true);
    setThankYouCustomMessage('');
    setShowSendThankYouModal(true);
  };

  const handleSendCustomThankYou = async () => {
    if (!thankYouSourceId || !thankYouPatientId) return;

    try {
      setSendingThankYou(true);
      const response = await fetch(`${backendUrl}/api/inbound-referrals/send-custom-thank-you`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sourceId: thankYouSourceId,
          patientId: thankYouPatientId,
          sendEmail: thankYouSendEmail,
          sendSms: thankYouSendSms,
          customMessage: thankYouCustomMessage || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send thank you');
      }

      const results = data.results;
      let message = "Thank you sent!";
      if (results.email.sent && results.sms.sent) {
        message = "Thank you sent via email and text!";
      } else if (results.email.sent) {
        message = "Thank you sent via email!";
      } else if (results.sms.sent) {
        message = "Thank you sent via text!";
      }

      toast({ title: "Success", description: message });
      setShowSendThankYouModal(false);
      
      // Refresh source details
      if (selectedInboundSource) {
        await loadInboundSourceDetails(selectedInboundSource.id);
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSendingThankYou(false);
    }
  };

  const loadThankYouTemplates = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/inbound-referrals/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setThankYouTemplates(data.templates || []);
    } catch (error) {
      console.error('Load templates error:', error);
    }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;

    try {
      setSavingTemplate(true);
      const response = await fetch(`${backendUrl}/api/inbound-referrals/templates/${editingTemplate.templateType}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          templateType: editingTemplate.templateType,
          subject: editingTemplate.subject,
          greeting: editingTemplate.greeting,
          bodyText: editingTemplate.bodyText,
          closingText: editingTemplate.closingText
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save template');
      }

      toast({ title: "Success", description: "Template saved successfully!" });
      await loadThankYouTemplates();
      setShowThankYouTemplateModal(false);
      setEditingTemplate(null);
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSavingTemplate(false);
    }
  };

  const openTemplateEditor = async () => {
    await loadThankYouTemplates();
    setShowThankYouTemplateModal(true);
  };

  const loadReferrals = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/referrals/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setReferrals(data.referrals || []);
      setFilteredReferrals(data.referrals || []);
    } catch (error) {
      console.error('Load referrals error:', error);
      toast({ title: "Error", description: "Failed to load referrals", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/practice/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPatients(data.data || []);
      setFilteredPatients(data.data || []);
    } catch (error) {
      console.error('Load patients error:', error);
    }
  };

  const loadDentists = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/referrals/dentists`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDentists(data.dentists || []);
    } catch (error) {
      console.error('Load dentists error:', error);
    }
  };

  const loadSpecialties = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/referrals/specialties`);
      const data = await response.json();
      setSpecialties(data.specialties || []);
    } catch (error) {
      console.error('Load specialties error:', error);
    }
  };

  const loadReferralSources = async (specialty = null) => {
    try {
      console.log('Loading referral sources for specialty:', specialty);
      
      // Load from Manage Referral Dentists, filtered by specialty if provided
      const url = specialty 
        ? `${backendUrl}/api/referral-dentists/by-specialty/${encodeURIComponent(specialty)}`
        : `${backendUrl}/api/referral-dentists`;
      
      console.log('Fetching from:', url);
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      console.log('Received referral dentists:', data);
      
      // Transform referral dentists to match source format
      const sources = (data.data || []).map(dentist => ({
        id: dentist.id,
        name: `Dr. ${dentist.firstName} ${dentist.lastName}`,
        firstName: dentist.firstName,
        lastName: dentist.lastName,
        phone: dentist.phone || '',
        email: dentist.email || '',
        address: dentist.address || '',
        specialty: dentist.specialty,
        website: dentist.website || ''
      }));
      
      console.log('Transformed sources:', sources);
      
      setReferralSources(sources);
    } catch (error) {
      console.error('Load sources error:', error);
    }
  };

  const handleAddPatient = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/practice/patients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPatient)
      });

      if (!response.ok) throw new Error('Failed');

      toast({ title: "Success", description: "Patient added" });
      await loadPatients();
      setShowAddPatientModal(false);
      setNewPatient({ firstName: '', lastName: '', email: '', phone: '', dateOfBirth: '' });
    } catch (error) {
      toast({ title: "Error", description: "Failed to add patient", variant: "destructive" });
    }
  };

  const handleAddDentist = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/referrals/sources`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `${newDentist.firstName} ${newDentist.lastName}`.trim(),
          email: newDentist.email,
          specialty: 'General Dentistry',
          phone: '',
          address: '',
          notes: ''
        })
      });

      if (!response.ok) throw new Error('Failed');

      const data = await response.json();
      toast({ title: "Success", description: "Dentist added" });

      await loadDentists();
      setShowAddDentistModal(false);
      setNewDentist({ firstName: '', lastName: '', email: '', role: 'practice_staff' });
    } catch (error) {
      toast({ title: "Error", description: "Failed to add dentist", variant: "destructive" });
    }
  };

  const handleAddSource = async () => {
    try {
      // Use specialty from newSource if set, otherwise use selectedSpecialty
      const specialty = newSource.specialty || selectedSpecialty;
      
      if (!specialty) {
        toast({ title: "Error", description: "Please select a specialty", variant: "destructive" });
        return;
      }

      // Debug: Log what we have
      console.log('Adding source with data:', newSource);
      
      // Validate required fields
      if (!newSource.name || !newSource.name.trim()) {
        console.log('Validation failed: name is empty');
        toast({ title: "Error", description: "Please enter the specialist's name", variant: "destructive" });
        return;
      }

      if (!newSource.email || !newSource.email.trim()) {
        console.log('Validation failed: email is empty');
        toast({ title: "Error", description: "Please enter the specialist's email", variant: "destructive" });
        return;
      }

      // Parse name into firstName and lastName
      const nameParts = newSource.name.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || nameParts[0]; // If only one name, use it for both
      
      console.log('Parsed name:', { firstName, lastName });
      
      // Add to Manage Referral Dentists (new system)
      const referralDentistData = {
        firstName: firstName,
        lastName: lastName,
        email: newSource.email || '',
        phone: newSource.phone || '',
        specialty: specialty,
        website: newSource.website || '',
        address: newSource.address || ''
      };
      
      console.log('Sending to API:', referralDentistData);

      const response = await fetch(`${backendUrl}/api/referral-dentists`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(referralDentistData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add specialist');
      }

      const data = await response.json();
      toast({ title: "Success", description: "Referral specialist added successfully to your network" });
      
      // Reload sources with the specialty filter to show the newly added specialist
      await loadReferralSources(specialty);
      
      // Set the selected source to the newly created one
      setSelectedSource({
        id: data.data.id,
        name: `Dr. ${firstName} ${lastName}`,
        firstName: firstName,
        lastName: lastName,
        phone: newSource.phone || '',
        email: newSource.email || '',
        address: newSource.address || '',
        specialty: specialty,
        website: newSource.website || ''
      });
      
      setShowAddSourceModal(false);
      setNewSource({ name: '', phone: '', email: '', address: '', specialty: '' });
      setStep(4);
    } catch (error) {
      console.error('Add source error:', error);
      toast({ title: "Error", description: error.message || "Failed to add specialist", variant: "destructive" });
    }
  };

  const handleSubmitReferral = async () => {
    if (!selectedPatient || !selectedSource || !selectedDentist) {
      toast({ title: "Error", description: "Select patient, dentist, and specialist", variant: "destructive" });
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/referrals/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patientId: selectedPatient.id,
          referralSourceId: selectedSource.id,
          referralSourceName: selectedSource.name,
          specialty: selectedSpecialty,
          referredById: selectedDentist.id,
          referredByName: `${selectedDentist.firstName} ${selectedDentist.lastName}`,
          ...formData
        })
      });

      if (!response.ok) throw new Error('Failed');

      const data = await response.json();
      setCreatedReferralId(data.referralId);
      
      toast({ title: "Success", description: "Referral created successfully" });
      await loadReferrals();
      
      // Move to step 5 (success screen with actions)
      setStep(5);
    } catch (error) {
      toast({ title: "Error", description: "Failed to create referral", variant: "destructive" });
    }
  };

  const resetReferralForm = () => {
    setActiveView('list');
    setStep(1);
    setSelectedPatient(null);
    setSelectedDentist(null);
    setSelectedSpecialty('');
    setSelectedSource(null);
    setCreatedReferralId(null);
    setFormData({
      chiefConcern: '',
      requestedService: '',
      toothArea: '',
      urgency: 'routine',
      medicalAlerts: '',
      notesToSpecialist: '',
      images: []
    });
  };

  const handleViewReferral = async (referralId) => {
    try {
      const response = await fetch(`${backendUrl}/api/referrals/${referralId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      const referralWithDetails = {
        ...data.referral,
        practiceInfo: data.practice,
        patientInfo: data.patient,
        sourceInfo: data.source
      };
      
      setSelectedReferral(referralWithDetails);
      setShowViewModal(true);
      setIsEditing(false);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load referral", variant: "destructive" });
    }
  };

  const handleEditReferral = async (referralId) => {
    try {
      console.log('🔧 handleEditReferral called for:', referralId);
      
      // First set editing mode to true
      setIsEditing(true);
      
      const response = await fetch(`${backendUrl}/api/referrals/${referralId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      const referralWithDetails = {
        ...data.referral,
        practiceInfo: data.practice,
        patientInfo: data.patient,
        sourceInfo: data.source
      };
      
      console.log('🔧 Opening modal with referral data, isEditing should be true');
      setSelectedReferral(referralWithDetails);
      setShowViewModal(true);
    } catch (error) {
      console.error('Edit referral error:', error);
      setIsEditing(false);
      toast({ title: "Error", description: "Failed to load referral for editing", variant: "destructive" });
    }
  };

  const handleViewPdf = async (referralId, action = 'view') => {
    try {
      console.log('Starting PDF generation for referral:', referralId);
      
      toast({
        title: "Creating PDF",
        description: "Generating PDF document...",
      });

      // Check cache first
      let blob = pdfCacheRef.current[referralId];
      if (!blob) {
        const response = await fetch(`${backendUrl}/api/referrals/${referralId}/generate-pdf`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to generate PDF: ${response.status}`);
        }
        
        blob = await response.blob();
        // Cache for 5 minutes
        pdfCacheRef.current[referralId] = blob;
        setTimeout(() => { delete pdfCacheRef.current[referralId]; }, 300000);
      }
      
      console.log('PDF ready, size:', blob.size, 'bytes');
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `referral_${referralId.substring(0, 8)}.pdf`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      toast({
        title: "Success",
        description: "PDF downloaded successfully. Check your Downloads folder.",
        duration: 3000,
      });
      
    } catch (error) {
      console.error('PDF download error:', error);
      toast({ 
        title: "Error", 
        description: error.message || "Failed to download PDF", 
        variant: "destructive" 
      });
    }
  };

  const handleDownloadReferral = (referralId) => {
    handleViewPdf(referralId, 'download');
  };

  const handlePrintReferral = async (referralId) => {
    try {
      toast({
        title: "Preparing to Print",
        description: "Loading PDF for printing...",
      });

      // Check cache first
      let blob = pdfCacheRef.current[referralId];
      if (!blob) {
        const response = await fetch(`${backendUrl}/api/referrals/${referralId}/generate-pdf`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to generate PDF: ${response.status}`);
        }
        
        blob = await response.blob();
        // Cache for 5 minutes
        pdfCacheRef.current[referralId] = blob;
        setTimeout(() => { delete pdfCacheRef.current[referralId]; }, 300000);
      }
      
      const url = URL.createObjectURL(blob);
      
      // Create hidden iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = url;
      
      document.body.appendChild(iframe);
      
      iframe.onload = function() {
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            
            toast({
              title: "Print Dialog Opened",
              description: "Complete printing, then this will auto-cleanup",
              duration: 5000,
            });
            
            const cleanup = () => {
              try {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                }
                URL.revokeObjectURL(url);
              } catch (e) {
                console.error('Cleanup error:', e);
              }
            };
            
            setTimeout(cleanup, 120000);
            
            window.addEventListener('focus', function focusHandler() {
              setTimeout(() => {
                cleanup();
                window.removeEventListener('focus', focusHandler);
              }, 1000);
            }, { once: true });
            
          } catch (e) {
            console.error('Print error:', e);
            const link = document.createElement('a');
            link.href = url;
            link.download = `referral_${referralId.substring(0, 8)}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            document.body.removeChild(iframe);
            URL.revokeObjectURL(url);
            
            toast({
              title: "Print Not Supported",
              description: "PDF downloaded instead. Open it to print.",
              duration: 3000,
            });
          }
        }, 500);
      };
      
    } catch (error) {
      console.error('Print error:', error);
      toast({ 
        title: "Error", 
        description: error.message || "Failed to print PDF", 
        variant: "destructive" 
      });
    }
  };

  const handleEmailReferralClick = (referralId) => {
    // Get referral details to show email addresses
    const referral = referrals.find(r => r.id === referralId);
    if (referral) {
      setEmailModalReferralId(referralId);
      setSendToSpecialist(true); // Default to specialist
      setSendToPatient(false);
      setShowEmailModal(true);
    }
  };

  const handleEmailReferral = async () => {
    if (!emailModalReferralId) return;
    
    // Validate at least one recipient selected
    if (!sendToSpecialist && !sendToPatient) {
      toast({ 
        title: "Error", 
        description: "Please select at least one recipient", 
        variant: "destructive" 
      });
      return;
    }

    try {
      setEmailingReferral(true);
      
      const response = await fetch(`${backendUrl}/api/referrals/${emailModalReferralId}/email?send_to_specialist=${sendToSpecialist}&send_to_patient=${sendToPatient}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send email');
      }
      
      toast({ 
        title: "Success", 
        description: data.message || "Referral emailed successfully" 
      });
      
      // Close email modal
      setShowEmailModal(false);
      setEmailModalReferralId(null);
      
      // If view modal is open, close it and return to list
      if (showViewModal) {
        setShowViewModal(false);
        setSelectedReferral(null);
        setIsEditing(false);
      }
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.message || "Failed to email referral", 
        variant: "destructive" 
      });
    } finally {
      setEmailingReferral(false);
    }
  };

  const handleUpdateReferral = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/referrals/${selectedReferral.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chiefConcern: selectedReferral.chiefConcern,
          requestedService: selectedReferral.requestedService,
          toothArea: selectedReferral.toothArea,
          urgency: selectedReferral.urgency,
          medicalAlerts: selectedReferral.medicalAlerts,
          notesToSpecialist: selectedReferral.notesToSpecialist,
          images: selectedReferral.images || []
        })
      });

      if (!response.ok) throw new Error('Failed');

      toast({ title: "Success", description: "Referral updated successfully with all changes" });
      setIsEditing(false);
      await loadReferrals();
      // Reload the referral to show updated data
      await handleViewReferral(selectedReferral.id);
    } catch (error) {
      console.error('Update error:', error);
      toast({ title: "Error", description: "Failed to update referral", variant: "destructive" });
    }
  };

  // SMS Functions
  const handleTextReferralClick = (referral) => {
    setSmsReferral(referral);
    setSmsRecipient('patient');
    setShowSmsModal(true);
  };

  const sendReferralSMS = async () => {
    if (!smsReferral) return;

    const recipients = [];
    
    // Use phone numbers directly from smsReferral if provided (success screen path),
    // otherwise fall back to looking them up from arrays (list view path)
    const patientPhone = smsReferral.patientPhone 
      || (() => { const p = patients.find(p => p.id === smsReferral.patientId); return p?.cellPhone || p?.phone; })();
    
    const specialistPhone = smsReferral.specialistPhone 
      || referralSources.find(s => s.id === smsReferral.referralSourceId)?.phone;

    const specialistName = smsReferral.specialistName || smsReferral.referralSourceName;
    const patientId = smsReferral.patientId || selectedPatient?.id;

    if (smsRecipient === 'patient' || smsRecipient === 'both') {
      if (!patientPhone) {
        toast({ title: "No Phone Number", description: "This patient doesn't have a phone number on file. Please add one in Patient Management → Edit Patient.", variant: "destructive", duration: 5000 });
        return;
      }
      recipients.push({ type: 'patient', phone: patientPhone, name: smsReferral.patientName });
    }

    if (smsRecipient === 'dentist' || smsRecipient === 'both') {
      if (!specialistPhone) {
        toast({ title: "Error", description: "Specialist does not have a phone number", variant: "destructive" });
        return;
      }
      recipients.push({ type: 'dentist', phone: specialistPhone, name: specialistName });
    }

    try {
      setSendingSMS(true);
      let successCount = 0;

      for (const recipient of recipients) {
        const response = await fetch(`${backendUrl}/api/sms/send-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            patient_id: patientId,
            document_type: 'referral',
            document_id: smsReferral.id,
            phone_number: recipient.phone
          })
        });

        if (response.ok) {
          successCount++;
        }
      }

      if (successCount === recipients.length) {
        toast({ 
          title: "Text Sent!", 
          description: `Referral texted to ${recipients.map(r => r.name).join(' and ')}`
        });
      } else {
        toast({ title: "Partial Success", description: `Sent ${successCount} of ${recipients.length} texts` });
      }

      setShowSmsModal(false);
      setSmsReferral(null);
    } catch (error) {
      console.error('SMS error:', error);
      toast({ title: "Error", description: "Failed to send text", variant: "destructive" });
    } finally {
      setSendingSMS(false);
    }
  };

  const handleDeleteReferral = async () => {
    if (!referralToDelete) return;

    try {
      const response = await fetch(`${backendUrl}/api/referrals/${referralToDelete}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed');

      toast({ title: "Success", description: "Referral deleted successfully" });
      
      // Close modals and refresh
      setShowDeleteConfirm(false);
      setShowViewModal(false);
      setReferralToDelete(null);
      setSelectedReferral(null);
      setIsEditing(false);
      
      // If we're on step 5 (just created), go back to list view
      if (step === 5) {
        setActiveView('list');
        setStep(1);
        setCreatedReferralId(null);
      }
      
      await loadReferrals();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete referral", variant: "destructive" });
    }
  };

  const confirmDelete = (referralId) => {
    setReferralToDelete(referralId);
    setShowDeleteConfirm(true);
  };

  const handleAskDan = async () => {
    if (!danInput.trim()) return;

    const currentMessage = danInput.trim();
    const userMessage = { role: 'user', content: currentMessage };
    const updatedHistory = [...danMessages, userMessage];
    
    setDanMessages(updatedHistory);
    setDanInput('');
    setDanLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/ask-dan/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: currentMessage,
          conversationHistory: danMessages
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.response) {
        setDanMessages([...updatedHistory, { role: 'assistant', content: data.response }]);
      } else {
        throw new Error('Invalid response from AI');
      }
    } catch (error) {
      console.error('Ask DAN error:', error);
      toast({ 
        title: "Error", 
        description: error.message || "Failed to get AI response", 
        variant: "destructive" 
      });
      // Remove the user message if request failed
      setDanMessages(danMessages);
    } finally {
      setDanLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    processImageFiles(files);
  };

  const processImageFiles = async (files) => {
    if (files.length === 0) return;

    // Limit to 10 images
    if (formData.images.length + files.length > 10) {
      toast({ 
        title: "Too many images", 
        description: "Maximum 10 images allowed per referral", 
        variant: "destructive" 
      });
      return;
    }

    setUploadingImages(true);

    try {
      const imagePromises = files.map(file => {
        return new Promise((resolve, reject) => {
          // Check file size (max 10MB per image)
          if (file.size > 10 * 1024 * 1024) {
            toast({ 
              title: "Image too large", 
              description: `${file.name} exceeds 10MB limit`, 
              variant: "destructive" 
            });
            reject(new Error('File too large'));
            return;
          }

          const reader = new FileReader();
          reader.onload = (e) => {
            resolve({
              name: file.name,
              data: e.target.result,
              type: file.type,
              size: file.size
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      const uploadedImages = await Promise.all(imagePromises);
      setFormData({
        ...formData,
        images: [...formData.images, ...uploadedImages]
      });
      
      toast({ 
        title: "Success", 
        description: `${uploadedImages.length} image(s) uploaded` 
      });
    } catch (error) {
      console.error('Image upload error:', error);
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const getSpecialtyColor = (specialty) => {
    const colors = {
      'General Dentistry': 'bg-blue-100 text-blue-800',
      'Orthodontics': 'bg-pink-100 text-pink-800',
      'Periodontics': 'bg-green-100 text-green-800',
      'Endodontics': 'bg-purple-100 text-purple-800',
      'Oral Surgery': 'bg-red-100 text-red-800',
      'Prosthodontics': 'bg-orange-100 text-orange-800',
      'Pediatric Dentistry': 'bg-yellow-100 text-yellow-800',
      'Medical': 'bg-teal-100 text-teal-800',
      'Oral Pathology': 'bg-indigo-100 text-indigo-800',
    };
    return colors[specialty] || 'bg-gray-100 text-gray-800';
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      'urgent': 'bg-red-100 text-red-800',
      'priority': 'bg-yellow-100 text-yellow-800',
      'routine': 'bg-green-100 text-green-800',
    };
    return colors[urgency] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading referrals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header - Matching Procedure & Informed Consent */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/practice/dashboard')}
                className="flex items-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Referral Management</h1>
                <p className="text-gray-600">Manage and track patient referrals</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">{practice?.name}</p>
              <p className="text-xs text-gray-500">Practice Administrator</p>
            </div>
          </div>

          {/* Ask Dr. G Helper Section */}
          <div className="max-w-7xl mx-auto px-4 mt-4">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 flex items-center justify-between border border-blue-200">
              <div className="flex items-center space-x-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_dental-dashboard-3/artifacts/a73bkdcb_a-cartoon-style-illustration-of-a-friend_BHWWQ3RORgyazFo06PAsDw_NyHovGrkR5m-Yi-_Gwdm5Q.png"
                  alt="Dr. G"
                  className="w-16 h-16 rounded-full shadow-lg"
                />
                <div className="text-center">
                  <p className="text-sm text-gray-700 font-medium">
                    Need Help Finding A Dentist?
                  </p>
                  <Button 
                    onClick={() => setShowAskDan(!showAskDan)} 
                    variant="outline"
                    size="sm"
                    className="mt-1"
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Click Here
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 pt-6">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setMainTab('to')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              mainTab === 'to'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="referral-to-tab"
          >
            <div className="flex items-center">
              <Send className="h-4 w-4 mr-2" />
              Referral To
            </div>
            <p className="text-xs font-normal mt-0.5 text-gray-400">Refer patients to specialists</p>
          </button>
          <button
            onClick={() => setMainTab('from')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              mainTab === 'from'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="referral-from-tab"
          >
            <div className="flex items-center">
              <Gift className="h-4 w-4 mr-2" />
              Referral From
            </div>
            <p className="text-xs font-normal mt-0.5 text-gray-400">People who referred patients to you</p>
          </button>
          <button
            onClick={() => navigate('/referral-analytics')}
            className="px-6 py-3 font-medium text-sm border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
            data-testid="referral-analytics-tab"
          >
            <div className="flex items-center">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </div>
            <p className="text-xs font-normal mt-0.5 text-gray-400">Acquisition by channel</p>
          </button>
        </div>
      </div>

      {/* Main Content based on Tab */}
      {mainTab === 'to' ? (
        // ==================== REFERRAL TO (Outbound) ====================
        <>
          {activeView === 'list' ? (
        <>
          {/* Search and New Referral Button */}
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex items-center gap-4">
              {referrals.length > 0 && (
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    type="text"
                    placeholder="Search existing referrals..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              )}
              <Button
                onClick={() => {
                  setActiveView('create');
                  setStep(1);
                }}
                className="flex items-center whitespace-nowrap"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Referral
              </Button>
              <AIReferralButton className="whitespace-nowrap" onSent={() => loadReferrals()} />
            </div>
          </div>

          {/* Referrals Grid - Matching Card Layout */}
          <div className="max-w-7xl mx-auto px-4 pb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredReferrals.map((referral) => (
                <Card key={referral.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{referral.patientName}</CardTitle>
                      <Badge className={getUrgencyColor(referral.urgency)}>
                        {referral.urgency?.toUpperCase()}
                      </Badge>
                    </div>
                    <Badge className={getSpecialtyColor(referral.specialty)} style={{ marginTop: '8px' }}>
                      {referral.specialty}
                    </Badge>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-2 text-sm">
                        <Building className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-gray-700">{referral.referralSourceName}</p>
                          <p className="text-gray-500 text-xs">Referred To</p>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {referral.chiefConcern || 'No concern specified'}
                      </p>
                      
                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <Clock className="h-3 w-3" />
                        <span>{new Date(referral.createdAt).toLocaleDateString()}</span>
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        <Button
                          size="sm"
                          onClick={() => handleViewReferral(referral.id)}
                          className="flex-1"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditReferral(referral.id)}
                          className="flex-1"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEmailReferralClick(referral.id)}
                          className="flex-1"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Email
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleTextReferralClick(referral)}
                          className="flex-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Text
                        </Button>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePrintReferral(referral.id)}
                          className="flex-1"
                        >
                          <Printer className="h-4 w-4 mr-2" />
                          Print
                        </Button>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDelete(referral.id);
                        }}
                        className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Empty State - Matching Other Libraries */}
            {filteredReferrals.length === 0 && !loading && (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No referrals found</h3>
                <p className="text-gray-600">
                  {searchQuery ? 'Try adjusting your search terms' : 'No referrals created yet'}
                </p>
                {!searchQuery && (
                  <Button onClick={() => setActiveView('create')} className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Referral
                  </Button>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        // Create Referral View - Full Page Card
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>{step === 5 ? 'Referral Created' : 'Create New Referral'}</CardTitle>
                  {step < 5 && <p className="text-sm text-gray-600 mt-1">Step {step} of 4</p>}
                </div>
                {step < 5 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setActiveView('list');
                      setStep(1);
                      setSelectedPatient(null);
                      setSelectedDentist(null);
                      setSelectedSpecialty('');
                      setSelectedSource(null);
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Step 1: Patient Selection */}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="flex justify-between mb-4">
                    <h3 className="text-lg font-semibold">Select Patient</h3>
                    <Button onClick={() => setShowAddPatientModal(true)} size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Patient
                    </Button>
                  </div>
                  
                  {/* Search box - only show when no patient selected */}
                  {!selectedPatient && (
                    <input
                      type="text"
                      placeholder="Type patient name to search..."
                      value={patientSearchTerm}
                      onChange={(e) => setPatientSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                    />
                  )}
                  
                  {/* Selected Patient Display */}
                  {selectedPatient && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between mb-4">
                      <div>
                        <p className="font-medium text-blue-900">
                          {selectedPatient.firstName} {selectedPatient.lastName}
                        </p>
                        <p className="text-sm text-blue-700">{selectedPatient.email}</p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setSelectedPatient(null);
                          setPatientSearchTerm('');
                        }}
                        className="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                      >
                        Change Patient
                      </button>
                    </div>
                  )}
                  
                  {/* Clickable Patient List - Show when not selected */}
                  {!selectedPatient && patients.length > 0 && (
                    <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto mb-4">
                      {filteredPatients.length > 0 ? (
                        filteredPatients.slice(0, 50).map((patient) => {
                          // Check for duplicate names
                          const duplicates = filteredPatients.filter(p => 
                            p.firstName === patient.firstName && p.lastName === patient.lastName
                          );
                          const showEmail = duplicates.length > 1;
                          
                          return (
                            <div
                              key={patient.id}
                              onClick={() => {
                                setSelectedPatient(patient);
                                setPatientSearchTerm('');
                              }}
                              className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                            >
                              <p className="font-medium text-gray-900">
                                {patient.firstName} {patient.lastName}
                              </p>
                              {showEmail && patient.email && (
                                <p className="text-sm text-gray-600">{patient.email}</p>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="p-3 text-center text-gray-500">
                          No patients match &quot;{patientSearchTerm}&quot;
                        </div>
                      )}
                    </div>
                  )}
                  
                  {!selectedPatient && filteredPatients.length > 0 && (
                    <p className="text-xs text-gray-600 mb-4">
                      Showing {Math.min(filteredPatients.length, 50)} of {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''}{patientSearchTerm ? ' matching your search' : ''} - click to select
                    </p>
                  )}
                  
                  {patients.length === 0 && !patientSearchTerm && (
                    <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                      <User className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600 mb-2">No patients yet</p>
                      <Button onClick={() => setShowAddPatientModal(true)} className="mt-4">
                        Add Your First Patient
                      </Button>
                    </div>
                  )}
                  
                  <div className="flex justify-end pt-4 border-t mt-4">
                    <Button
                      onClick={() => setStep(2)}
                      disabled={!selectedPatient}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2: Referring Dentist (The dentist in your practice making the referral) */}
              {step === 2 && (
                <div className="space-y-6">
                  <Button variant="outline" onClick={() => setStep(1)}>← Back</Button>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p><strong>Patient:</strong> {selectedPatient?.firstName} {selectedPatient?.lastName}</p>
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="block font-semibold">Referring Dentist (From Your Practice) *</label>
                      <Button onClick={() => setShowAddDentistModal(true)} size="sm" variant="outline">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Dentist
                      </Button>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Select the dentist in your practice who is making this referral</p>
                    <select
                      value={selectedDentist?.id || ''}
                      onChange={(e) => {
                        const dentist = dentists.find(d => d.id === e.target.value);
                        setSelectedDentist(dentist);
                      }}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Select dentist from your practice...</option>
                      {dentists.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.firstName} {d.lastName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <Button 
                    onClick={() => setStep(3)} 
                    disabled={!selectedDentist}
                  >
                    Next
                  </Button>
                </div>
              )}

              {/* Step 3: Refer to Specialty & Select Specialist */}
              {step === 3 && (
                <div className="space-y-6">
                  <Button variant="outline" onClick={() => setStep(2)}>← Back</Button>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p><strong>Patient:</strong> {selectedPatient?.firstName} {selectedPatient?.lastName}</p>
                    <p><strong>Referring Dentist:</strong> {selectedDentist?.firstName} {selectedDentist?.lastName}</p>
                  </div>

                  <div>
                    <label className="block mb-2 font-semibold">Refer to Specialty *</label>
                    <p className="text-sm text-gray-600 mb-2">What type of specialist do you want to refer this patient to?</p>
                    <select
                      value={selectedSpecialty}
                      onChange={(e) => setSelectedSpecialty(e.target.value)}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Choose specialty...</option>
                      {specialties.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  {selectedSpecialty && (
                    <>
                      {/* Ask Dr. G Helper */}
                      <div className="bg-gradient-to-r from-cyan-50 to-cyan-100 rounded-lg p-4 border border-cyan-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_dental-dashboard-3/artifacts/a73bkdcb_a-cartoon-style-illustration-of-a-friend_BHWWQ3RORgyazFo06PAsDw_NyHovGrkR5m-Yi-_Gwdm5Q.png"
                              alt="Dr. G"
                              className="w-16 h-16 rounded-full shadow-lg"
                            />
                            <div>
                              <p className="text-sm text-gray-700 font-medium mb-1">
                                Need help finding a referral dentist?
                              </p>
                              <p className="text-xs text-gray-600">
                                That&apos;s where we come in. Type in the name, location, and specialty.
                              </p>
                              <Button 
                                onClick={() => setShowAskDan(!showAskDan)} 
                                variant="outline"
                                size="sm"
                                className="mt-2"
                              >
                                <MessageCircle className="h-4 w-4 mr-2" />
                                Ask Dr. G
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between mb-2">
                          <label className="block font-semibold">Select Specialist *</label>
                          <Button onClick={() => setShowAddSourceModal(true)} size="sm" variant="outline">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Specialist
                          </Button>
                        </div>
                        <select
                          value={selectedSource?.id || ''}
                          onChange={(e) => {
                            const source = referralSources.find(s => s.id === e.target.value);
                            setSelectedSource(source);
                          }}
                          className="w-full p-2 border rounded"
                        >
                          <option value="">Select specialist...</option>
                          {referralSources.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name} {s.specialty ? `(${s.specialty})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  <Button 
                    onClick={() => setStep(4)} 
                    disabled={!selectedSpecialty || !selectedSource}
                  >
                    Next
                  </Button>
                </div>
              )}

              {/* Step 4: Clinical Details */}
              {step === 4 && (
                <div className="space-y-6">
                  <Button variant="outline" onClick={() => setStep(3)}>← Back</Button>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p><strong>Patient:</strong> {selectedPatient?.firstName} {selectedPatient?.lastName}</p>
                    <p><strong>Referral Source:</strong> {selectedDentist?.firstName} {selectedDentist?.lastName}</p>
                    <p><strong>Referring To:</strong> {selectedSource?.name} ({selectedSpecialty})</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block mb-2 font-semibold">Chief Concern *</label>
                      <textarea
                        value={formData.chiefConcern}
                        onChange={(e) => setFormData({...formData, chiefConcern: e.target.value})}
                        className="w-full p-2 border rounded"
                        rows={3}
                        placeholder="Describe the main concern..."
                      />
                    </div>

                    <div>
                      <label className="block mb-2 font-semibold">Requested Service *</label>
                      <Input
                        value={formData.requestedService}
                        onChange={(e) => setFormData({...formData, requestedService: e.target.value})}
                        placeholder="e.g., Evaluation and treatment"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block mb-2 font-semibold">Tooth/Area</label>
                        <Input
                          value={formData.toothArea}
                          onChange={(e) => setFormData({...formData, toothArea: e.target.value})}
                          placeholder="e.g., #14"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Urgency</label>
                        <select
                          value={formData.urgency}
                          onChange={(e) => setFormData({...formData, urgency: e.target.value})}
                          className="w-full p-2 border rounded"
                        >
                          <option value="routine">Routine</option>
                          <option value="priority">Priority</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block mb-2 font-semibold">Medical Alerts</label>
                      <textarea
                        value={formData.medicalAlerts}
                        onChange={(e) => setFormData({...formData, medicalAlerts: e.target.value})}
                        className="w-full p-2 border rounded"
                        rows={2}
                        placeholder="Any medical alerts or allergies..."
                      />
                    </div>

                    <div>
                      <label className="block mb-2 font-semibold">Notes to Specialist</label>
                      <textarea
                        value={formData.notesToSpecialist}
                        onChange={(e) => setFormData({...formData, notesToSpecialist: e.target.value})}
                        className="w-full p-2 border rounded"
                        rows={3}
                        placeholder="Additional notes for the specialist..."
                      />
                    </div>

                    {/* Image Upload Section */}
                    <div className="border-t pt-4">
                      <label className="block mb-2 font-semibold">Attach Images (Optional)</label>
                      <p className="text-sm text-gray-600 mb-3">
                        Upload X-rays, clinical photos, or other diagnostic images (Max 10 images, 10MB each)
                      </p>
                      
                      {/* Upload Button with Drag & Drop */}
                      <div className="mb-4">
                        <div 
                          className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition-colors text-center cursor-pointer"
                          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('border-blue-500', 'bg-blue-50'); }}
                          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50'); }}
                          onDrop={(e) => { 
                            e.preventDefault(); e.stopPropagation(); 
                            e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                            const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
                            processImageFiles(files);
                          }}
                          onClick={() => document.getElementById('referral-image-input')?.click()}
                        >
                          <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm text-gray-600">
                            Click to upload or drag and drop
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            PNG, JPG, JPEG up to 10MB
                          </p>
                        </div>
                        <input
                          id="referral-image-input"
                          type="file"
                          accept="image/png,image/jpeg,image/jpg"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploadingImages || formData.images.length >= 10}
                        />
                      </div>

                      {/* Image Preview Grid */}
                      {formData.images.length > 0 && (
                        <div className="grid grid-cols-3 gap-3">
                          {formData.images.map((image, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={image.data}
                                alt={image.name}
                                className="w-full h-24 object-cover rounded border"
                              />
                              <button
                                onClick={() => removeImage(index)}
                                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="h-4 w-4" />
                              </button>
                              <p className="text-xs text-gray-600 mt-1 truncate">{image.name}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {uploadingImages && (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                          <p className="text-sm text-gray-600 mt-2">Uploading images...</p>
                        </div>
                      )}
                    </div>

                    <Button 
                      onClick={handleSubmitReferral}
                      disabled={!formData.chiefConcern || !formData.requestedService || uploadingImages}
                      className="w-full"
                    >
                      Create Referral
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 5: Success - View/Print/Email Options */}
              {step === 5 && createdReferralId && (
                <div className="space-y-6">
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Referral Created Successfully!</h3>
                    <p className="text-gray-600 mb-6">
                      The referral for {selectedPatient?.firstName} {selectedPatient?.lastName} has been created.
                    </p>
                  </div>

                  <div className="bg-blue-50 p-6 rounded-lg">
                    <h4 className="font-semibold mb-3">Referral Summary:</h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Patient:</strong> {selectedPatient?.firstName} {selectedPatient?.lastName}</p>
                      <p><strong>Referring Dentist:</strong> {selectedDentist?.firstName} {selectedDentist?.lastName}</p>
                      <p><strong>Specialty:</strong> {selectedSpecialty}</p>
                      <p><strong>Specialist:</strong> {selectedSource?.name}</p>
                      <p><strong>Chief Concern:</strong> {formData.chiefConcern}</p>
                      <p><strong>Urgency:</strong> <span className={`px-2 py-1 rounded text-xs font-bold ${
                        formData.urgency === 'urgent' ? 'bg-red-100 text-red-800' :
                        formData.urgency === 'priority' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>{formData.urgency.toUpperCase()}</span></p>
                    </div>
                  </div>

                  {/* Needing Help Section with Dr. G */}
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-center space-x-3">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_dental-dashboard-3/artifacts/a73bkdcb_a-cartoon-style-illustration-of-a-friend_BHWWQ3RORgyazFo06PAsDw_NyHovGrkR5m-Yi-_Gwdm5Q.png"
                        alt="Dr. G"
                        className="w-12 h-12 rounded-full bg-white p-1"
                      />
                      <div className="text-left">
                        <p className="text-sm font-semibold text-blue-900">Need help finding a specialist?</p>
                        <p className="text-xs text-blue-700">Ask Dr. G for recommendations!</p>
                      </div>
                      <Button 
                        onClick={() => setShowAskDan(true)}
                        size="sm"
                        variant="outline"
                        className="ml-auto border-blue-300 text-blue-700 hover:bg-blue-200"
                      >
                        Ask Dr. G
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <p className="text-center text-gray-600 font-medium">What would you like to do next?</p>
                    
                    <Button 
                      onClick={() => handleViewReferral(createdReferralId)}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      size="lg"
                    >
                      <Eye className="h-5 w-5 mr-2" />
                      View Referral Details
                    </Button>

                    <div className="grid grid-cols-2 gap-3">
                      <Button 
                        onClick={() => handleDownloadReferral(createdReferralId)}
                        variant="outline"
                        size="lg"
                      >
                        <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download PDF
                      </Button>

                      <Button 
                        onClick={() => handlePrintReferral(createdReferralId)}
                        variant="outline"
                        size="lg"
                      >
                        <Printer className="h-5 w-5 mr-2" />
                        Print PDF
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <Button 
                        onClick={() => handleEmailReferralClick(createdReferralId)}
                        variant="outline"
                        size="lg"
                      >
                        <Mail className="h-5 w-5 mr-2" />
                        Email
                      </Button>

                      <Button 
                        onClick={() => {
                          // Gather the specialist phone from all available sources
                          const sourcePhone = selectedSource?.phone 
                            || referralSources.find(s => s.id === selectedSource?.id)?.phone;
                          
                          if (!sourcePhone) {
                            toast({
                              title: "No Phone Number",
                              description: "The specialist doesn't have a phone number on file. Please add one in Manage Referral Dentists.",
                              variant: "destructive"
                            });
                            return;
                          }

                          setSmsReferral({
                            id: createdReferralId,
                            patientId: selectedPatient?.id,
                            patientName: `${selectedPatient?.firstName} ${selectedPatient?.lastName}`,
                            patientPhone: selectedPatient?.cellPhone || selectedPatient?.phone,
                            specialistName: selectedSource?.name,
                            specialistPhone: sourcePhone
                          });
                          setSmsRecipient('dentist');
                          setShowSmsModal(true);
                        }}
                        variant="outline"
                        size="lg"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        data-testid="text-dentist-btn"
                      >
                        <MessageSquare className="h-5 w-5 mr-2" />
                        Text Dentist
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <Button 
                        onClick={() => confirmDelete(createdReferralId)}
                        variant="outline"
                        size="lg"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-5 w-5 mr-2" />
                        Delete Referral
                      </Button>
                    </div>

                    <Button 
                      onClick={resetReferralForm}
                      variant="outline"
                      className="w-full mt-4"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Create Another Referral
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
        </>
      ) : (
        // ==================== REFERRAL FROM (Inbound) ====================
        <>
          {/* Search and Add Button */}
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={inboundSearchQuery}
                  onChange={(e) => setInboundSearchQuery(e.target.value)}
                  className="pl-10 pr-8"
                  data-testid="inbound-search-input"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  name="inbound-source-search"
                />
                {inboundSearchQuery && (
                  <button
                    type="button"
                    onClick={() => setInboundSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {/* Type Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600 whitespace-nowrap">Sort By Type:</span>
                <Select value={selectedTypeFilter} onValueChange={setSelectedTypeFilter}>
                  <SelectTrigger className="w-48" data-testid="inbound-type-filter">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {REFERRAL_SOURCE_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button onClick={() => setShowAddInboundSourceModal(true)} data-testid="add-inbound-source-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Source
                </Button>
                <Button variant="outline" onClick={openTemplateEditor} data-testid="edit-templates-btn">
                  <Settings className="h-4 w-4 mr-2" />
                  Templates
                </Button>
              </div>
            </div>
          </div>

          {/* Inbound Sources Grid */}
          <div className="max-w-7xl mx-auto px-4 pb-8">
            {inboundLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading referral sources...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredInboundSources.map((source) => (
                  <Card key={source.id} className="hover:shadow-lg transition-shadow" data-testid={`inbound-source-card-${source.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg flex items-center">
                          {getInboundTypeIcon(source.sourceType)}
                          <span className="ml-2">{source.name}</span>
                        </CardTitle>
                        <Badge className={getInboundTypeColor(source.sourceType)}>
                          {source.sourceType}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm text-gray-600 mb-4">
                        {source.email && (
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 mr-2 text-gray-400" />
                            <span className="truncate">{source.email}</span>
                          </div>
                        )}
                        {source.phone && (
                          <div className="flex items-center">
                            <Phone className="h-4 w-4 mr-2 text-gray-400" />
                            <span>{source.phone}</span>
                          </div>
                        )}
                        <div className="flex items-center text-green-600">
                          <Gift className="h-4 w-4 mr-2" />
                          <span>{source.totalReferrals || 0} referral{source.totalReferrals !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      
                      {/* Primary Actions */}
                      <div className="flex gap-2 mb-2">
                        <Button
                          size="sm"
                          onClick={() => openViewInboundSourceModal(source)}
                          className="flex-1"
                          data-testid={`view-inbound-source-${source.id}`}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openLinkPatientModal(source)}
                          className="flex-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                          data-testid={`add-referral-${source.id}`}
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Add Referral
                        </Button>
                      </div>
                      
                      {/* Secondary Actions */}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditInboundSourceModal(source)}
                          className="flex-1"
                          data-testid={`edit-inbound-source-${source.id}`}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDeleteInboundConfirm(source)}
                          className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-inbound-source-${source.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Empty State */}
            {filteredInboundSources.length === 0 && !inboundLoading && (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No referral sources found</h3>
                <p className="text-gray-600">
                  {inboundSearchQuery || selectedTypeFilter !== 'all' 
                    ? 'Try adjusting your filters' 
                    : 'Add your first referral source to start tracking who refers patients to your practice'}
                </p>
                {!inboundSearchQuery && selectedTypeFilter === 'all' && (
                  <Button onClick={() => setShowAddInboundSourceModal(true)} className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Referral Source
                  </Button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Add Patient Modal */}
      {showAddPatientModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Patient</h3>
              <button onClick={() => setShowAddPatientModal(false)}><X /></button>
            </div>
            <div className="space-y-4">
              <Input placeholder="First Name" value={newPatient.firstName}
                onChange={(e) => setNewPatient({...newPatient, firstName: e.target.value})} />
              <Input placeholder="Last Name" value={newPatient.lastName}
                onChange={(e) => setNewPatient({...newPatient, lastName: e.target.value})} />
              <Input placeholder="Email" value={newPatient.email}
                onChange={(e) => setNewPatient({...newPatient, email: e.target.value})} />
              <Input placeholder="Phone" value={newPatient.phone}
                onChange={(e) => setNewPatient({...newPatient, phone: e.target.value})} />
              <Input type="date" placeholder="Date of Birth" value={newPatient.dateOfBirth}
                onChange={(e) => setNewPatient({...newPatient, dateOfBirth: e.target.value})} />
              <Button onClick={handleAddPatient} className="w-full">Add</Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Dentist Modal */}
      {showAddDentistModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Referral Dentist</h3>
              <button onClick={() => setShowAddDentistModal(false)}><X /></button>
            </div>
            <div className="space-y-4">
              <Input placeholder="First Name" value={newDentist.firstName}
                onChange={(e) => setNewDentist({...newDentist, firstName: e.target.value})} />
              <Input placeholder="Last Name" value={newDentist.lastName}
                onChange={(e) => setNewDentist({...newDentist, lastName: e.target.value})} />
              <Input placeholder="Email" type="email" value={newDentist.email}
                onChange={(e) => setNewDentist({...newDentist, email: e.target.value})} />
              <Button onClick={handleAddDentist} className="w-full">Add</Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Source Modal */}
      {showAddSourceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Referral Specialist</h3>
              <button onClick={() => setShowAddSourceModal(false)}><X /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Specialty *</label>
                <select 
                  className="w-full p-2 border rounded"
                  value={newSource.specialty || selectedSpecialty || ''}
                  onChange={(e) => setNewSource({...newSource, specialty: e.target.value})}
                >
                  <option value="">Select specialty...</option>
                  {specialties.map(spec => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
                </select>
              </div>
              <Input placeholder="Specialist Name *" value={newSource.name}
                onChange={(e) => setNewSource({...newSource, name: e.target.value})} />
              <div>
                <Input placeholder="Email" type="email" value={newSource.email}
                  onChange={(e) => setNewSource({...newSource, email: e.target.value})} />
                <p className="text-xs text-gray-500 mt-1 italic">Note: Email required if you want to email referrals to this specialist</p>
              </div>
              <Input placeholder="Phone" value={newSource.phone}
                onChange={(e) => setNewSource({...newSource, phone: e.target.value})} />
              <Input placeholder="Address" value={newSource.address}
                onChange={(e) => setNewSource({...newSource, address: e.target.value})} />
              <Button 
                onClick={handleAddSource} 
                className="w-full"
                disabled={!newSource.name || !(newSource.specialty || selectedSpecialty)}
              >
                Add Specialist
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal - keeping existing professional format */}
      {showViewModal && selectedReferral && (() => {
        console.log('📋 Modal rendering - isEditing:', isEditing);
        return true;
      })() && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Header with Practice Logo */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
              {selectedReferral?.practiceInfo?.branding?.logo && (
                <div className="flex justify-center mb-4">
                  <img 
                    src={selectedReferral.practiceInfo.branding.logo} 
                    alt="Practice Logo" 
                    className="h-16 max-w-xs object-contain bg-white px-4 py-2 rounded"
                  />
                </div>
              )}
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold">
                    PATIENT REFERRAL
                    {isEditing && <span className="ml-3 text-yellow-300 text-base">(EDITING MODE)</span>}
                  </h3>
                  <p className="text-blue-100 text-sm mt-1">Referral ID: {selectedReferral.id.substring(0, 8).toUpperCase()}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {!isEditing && (
                    <Button size="sm" className="bg-white text-blue-600 hover:bg-blue-50" onClick={() => setIsEditing(true)}>
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  )}
                  {isEditing && (
                    <div className="bg-yellow-400 text-gray-900 px-3 py-1 rounded text-sm font-semibold">
                      ✏️ Editing
                    </div>
                  )}
                  <button 
                    onClick={() => { setShowViewModal(false); setSelectedReferral(null); setIsEditing(false); }}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 bg-gray-50">
              {/* Editing Mode Banner */}
              {isEditing && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mb-4 shadow-sm">
                  <div className="flex items-start">
                    <Edit className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-yellow-800 font-semibold mb-2">
                        You are now editing this referral.
                      </p>
                      <p className="text-yellow-700 text-sm mb-2">
                        <strong>Editable fields:</strong> Tooth/Area, Chief Concern, Requested Service, Medical Alerts, Notes to Specialist, Images (add or remove)
                      </p>
                      <p className="text-yellow-700 text-sm">
                        <strong>Cannot be changed:</strong> Patient information, Referring Dentist, Specialist, Referral Date, Urgency
                      </p>
                      <p className="text-yellow-700 text-xs mt-2 italic">
                        Scroll down to click "Save Changes" when done.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Date and Urgency Banner */}
              <div className="bg-white border-l-4 border-blue-600 p-4 rounded mb-6 shadow-sm">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-sm text-gray-600">Referral Date:</span>
                    <span className="ml-2 font-semibold">{new Date(selectedReferral.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Urgency:</span>
                    <span className={`ml-2 px-3 py-1 rounded-full text-sm font-bold ${
                      selectedReferral.urgency === 'urgent' ? 'bg-red-100 text-red-800' :
                      selectedReferral.urgency === 'priority' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {selectedReferral.urgency?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Referring Practice Section */}
              <div className="bg-white rounded-lg p-6 mb-6 shadow-sm border border-gray-200">
                <h4 className="text-lg font-bold text-blue-700 mb-4 flex items-center">
                  <div className="w-1 h-6 bg-blue-600 mr-3"></div>
                  REFERRING PRACTICE
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 font-semibold">Practice:</p>
                    <p className="text-gray-900">{selectedReferral?.practiceInfo?.practiceName || selectedReferral?.practiceInfo?.name || 'N/A'}</p>
                  </div>
                  {selectedReferral?.referredByName && (
                    <div>
                      <p className="text-gray-600 font-semibold">Referred By:</p>
                      <p className="text-gray-900 text-blue-700 font-medium">{selectedReferral.referredByName}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-600 font-semibold">Phone:</p>
                    <p className="text-gray-900">{selectedReferral?.practiceInfo?.phone || 'N/A'}</p>
                  </div>
                  {selectedReferral?.practiceInfo?.email && (
                    <div>
                      <p className="text-gray-600 font-semibold">Email:</p>
                      <p className="text-gray-900">{selectedReferral.practiceInfo.email}</p>
                    </div>
                  )}
                  {selectedReferral?.practiceInfo?.address && (
                    <div className="col-span-2">
                      <p className="text-gray-600 font-semibold">Address:</p>
                      <p className="text-gray-900">
                        {typeof selectedReferral.practiceInfo.address === 'string' 
                          ? selectedReferral.practiceInfo.address 
                          : `${selectedReferral.practiceInfo.address.street || ''}, ${selectedReferral.practiceInfo.address.city || ''}, ${selectedReferral.practiceInfo.address.state || ''} ${selectedReferral.practiceInfo.address.zipCode || ''}`
                        }
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Referred To Section */}
              <div className="bg-white rounded-lg p-6 mb-6 shadow-sm border border-gray-200">
                <h4 className="text-lg font-bold text-blue-700 mb-4 flex items-center">
                  <div className="w-1 h-6 bg-blue-600 mr-3"></div>
                  REFERRED TO
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 font-semibold">Name:</p>
                    <p className="text-gray-900">{selectedReferral.referralSourceName}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-semibold">Specialty:</p>
                    <p className="text-gray-900">{selectedReferral.specialty}</p>
                  </div>
                  {selectedReferral?.sourceInfo?.phone && (
                    <div>
                      <p className="text-gray-600 font-semibold">Phone:</p>
                      <p className="text-gray-900">{selectedReferral.sourceInfo.phone}</p>
                    </div>
                  )}
                  {selectedReferral?.sourceInfo?.email && (
                    <div>
                      <p className="text-gray-600 font-semibold">Email:</p>
                      <p className="text-gray-900">{selectedReferral.sourceInfo.email}</p>
                    </div>
                  )}
                  {selectedReferral?.sourceInfo?.address && (
                    <div className="col-span-2">
                      <p className="text-gray-600 font-semibold">Address:</p>
                      <p className="text-gray-900">{selectedReferral.sourceInfo.address}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Patient Information */}
              <div className="bg-white rounded-lg p-6 mb-6 shadow-sm border border-gray-200">
                <h4 className="text-lg font-bold text-blue-700 mb-4 flex items-center">
                  <div className="w-1 h-6 bg-blue-600 mr-3"></div>
                  PATIENT INFORMATION
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="col-span-2">
                    <p className="text-gray-600 font-semibold">Name:</p>
                    <p className="text-gray-900 text-lg">{selectedReferral.patientName}</p>
                  </div>
                  {selectedReferral?.patientInfo?.dateOfBirth && (
                    <div>
                      <p className="text-gray-600 font-semibold">Date of Birth:</p>
                      <p className="text-gray-900">{selectedReferral.patientInfo.dateOfBirth}</p>
                    </div>
                  )}
                  {selectedReferral?.patientInfo?.phone && (
                    <div>
                      <p className="text-gray-600 font-semibold">Phone:</p>
                      <p className="text-gray-900">{selectedReferral.patientInfo.phone}</p>
                    </div>
                  )}
                  {selectedReferral?.patientInfo?.email && (
                    <div className="col-span-2">
                      <p className="text-gray-600 font-semibold">Email:</p>
                      <p className="text-gray-900">{selectedReferral.patientInfo.email}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Clinical Information */}
              <div className="bg-white rounded-lg p-6 mb-6 shadow-sm border border-gray-200">
                <h4 className="text-lg font-bold text-blue-700 mb-4 flex items-center">
                  <div className="w-1 h-6 bg-blue-600 mr-3"></div>
                  CLINICAL INFORMATION
                </h4>
                <div className="space-y-4">
                  {selectedReferral.toothArea && (
                    <div>
                      <p className="text-gray-600 font-semibold text-sm">Tooth/Area:</p>
                      {isEditing ? (
                        <Input
                          value={selectedReferral.toothArea}
                          onChange={(e) => setSelectedReferral({...selectedReferral, toothArea: e.target.value})}
                          className="mt-1"
                        />
                      ) : (
                        <p className="text-gray-900 ml-3">{selectedReferral.toothArea}</p>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <p className="text-gray-600 font-semibold text-sm">Chief Concern:</p>
                    {isEditing ? (
                      <textarea
                        value={selectedReferral.chiefConcern}
                        onChange={(e) => setSelectedReferral({...selectedReferral, chiefConcern: e.target.value})}
                        className="w-full p-3 border rounded mt-1"
                        rows={3}
                      />
                    ) : (
                      <p className="text-gray-900 ml-3">{selectedReferral.chiefConcern}</p>
                    )}
                  </div>

                  <div>
                    <p className="text-gray-600 font-semibold text-sm">Requested Service:</p>
                    {isEditing ? (
                      <Input
                        value={selectedReferral.requestedService}
                        onChange={(e) => setSelectedReferral({...selectedReferral, requestedService: e.target.value})}
                        className="mt-1"
                      />
                    ) : (
                      <p className="text-gray-900 ml-3">{selectedReferral.requestedService}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Medical Alerts */}
              {selectedReferral.medicalAlerts && (
                <div className="bg-red-50 rounded-lg p-6 mb-6 shadow-sm border-2 border-red-300">
                  <h4 className="text-lg font-bold text-red-700 mb-4 flex items-center">
                    <div className="w-1 h-6 bg-red-600 mr-3"></div>
                    ⚠️ MEDICAL ALERTS
                  </h4>
                  {isEditing ? (
                    <textarea
                      value={selectedReferral.medicalAlerts}
                      onChange={(e) => setSelectedReferral({...selectedReferral, medicalAlerts: e.target.value})}
                      className="w-full p-3 border border-red-300 rounded"
                      rows={2}
                    />
                  ) : (
                    <p className="text-red-800 ml-3 font-medium">{selectedReferral.medicalAlerts}</p>
                  )}
                </div>
              )}

              {/* Notes to Specialist */}
              {selectedReferral.notesToSpecialist && (
                <div className="bg-white rounded-lg p-6 mb-6 shadow-sm border border-gray-200">
                  <h4 className="text-lg font-bold text-blue-700 mb-4 flex items-center">
                    <div className="w-1 h-6 bg-blue-600 mr-3"></div>
                    NOTES TO SPECIALIST
                  </h4>
                  {isEditing ? (
                    <textarea
                      value={selectedReferral.notesToSpecialist}
                      onChange={(e) => setSelectedReferral({...selectedReferral, notesToSpecialist: e.target.value})}
                      className="w-full p-3 border rounded"
                      rows={3}
                    />
                  ) : (
                    <p className="text-gray-900 ml-3">{selectedReferral.notesToSpecialist}</p>
                  )}
                </div>
              )}

              {/* Attached Images */}
              {/* Images Section */}
              <div className="bg-white rounded-lg p-6 mb-6 shadow-sm border border-gray-200">
                <h4 className="text-lg font-bold text-blue-700 mb-4 flex items-center">
                  <div className="w-1 h-6 bg-blue-600 mr-3"></div>
                  ATTACHED IMAGES ({selectedReferral.images?.length || 0})
                </h4>
                
                {/* Existing Images */}
                {selectedReferral.images && selectedReferral.images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 ml-3 mb-4">
                    {selectedReferral.images.map((image, index) => (
                      <div key={index} className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow relative">
                        <img
                          src={image.data}
                          alt={image.name || `Image ${index + 1}`}
                          className="w-full h-48 object-cover cursor-pointer"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = image.data;
                            link.target = '_blank';
                            link.rel = 'noopener noreferrer';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        />
                        {isEditing && (
                          <button
                            onClick={() => {
                              const newImages = selectedReferral.images.filter((_, i) => i !== index);
                              setSelectedReferral({...selectedReferral, images: newImages});
                            }}
                            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                        <div className="p-2 bg-gray-50">
                          <p className="text-xs text-gray-600 truncate">{image.name || `Image ${index + 1}`}</p>
                          <p className="text-xs text-gray-500">{(image.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add Images in Edit Mode */}
                {isEditing && (
                  <div className="ml-3 mt-4">
                    <div 
                      className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-500 transition-colors"
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('border-blue-500', 'bg-blue-50'); }}
                      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50'); }}
                      onDrop={async (e) => { 
                        e.preventDefault(); e.stopPropagation(); 
                        e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
                        if (!files.length) return;
                        const currentImageCount = selectedReferral.images?.length || 0;
                        if (currentImageCount + files.length > 10) {
                          toast({ title: "Too many images", description: "Maximum 10 images allowed per referral", variant: "destructive" });
                          return;
                        }
                        setUploadingImages(true);
                        try {
                          const uploadedImages = [];
                          for (const file of files) {
                            if (file.size > 10 * 1024 * 1024) {
                              toast({ title: "File too large", description: `${file.name} exceeds 10MB limit`, variant: "destructive" });
                              continue;
                            }
                            const reader = new FileReader();
                            const imageData = await new Promise((resolve) => { reader.onload = (ev) => resolve(ev.target.result); reader.readAsDataURL(file); });
                            uploadedImages.push({ name: file.name, size: file.size, data: imageData });
                          }
                          setSelectedReferral({ ...selectedReferral, images: [...(selectedReferral.images || []), ...uploadedImages] });
                          toast({ title: "Images added", description: `${uploadedImages.length} image(s) added. Save to confirm.` });
                        } catch (err) { console.error(err); } finally { setUploadingImages(false); }
                      }}
                    >
                      <div className="flex items-center justify-center">
                        <label className="flex flex-col items-center cursor-pointer">
                          <Upload className="h-8 w-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-600 mb-1">Upload Additional Images</span>
                          <span className="text-xs text-gray-500">X-rays, photos, or diagnostic images</span>
                          <span className="text-xs text-gray-400 mt-1">Max 10 images total, 10MB each</span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={async (e) => {
                              const files = Array.from(e.target.files);
                              if (!files.length) return;
                              
                              const currentImageCount = selectedReferral.images?.length || 0;
                              if (currentImageCount + files.length > 10) {
                                toast({
                                  title: "Too many images",
                                  description: "Maximum 10 images allowed per referral",
                                  variant: "destructive"
                                });
                                return;
                              }
                              
                              setUploadingImages(true);
                              try {
                                const uploadedImages = [];
                                for (const file of files) {
                                  if (file.size > 10 * 1024 * 1024) {
                                    toast({
                                      title: "File too large",
                                      description: `${file.name} exceeds 10MB limit`,
                                      variant: "destructive"
                                    });
                                    continue;
                                  }
                                  
                                  const reader = new FileReader();
                                  const imageData = await new Promise((resolve) => {
                                    reader.onload = (e) => resolve(e.target.result);
                                    reader.readAsDataURL(file);
                                  });
                                  
                                  uploadedImages.push({
                                    name: file.name,
                                    size: file.size,
                                    data: imageData
                                  });
                                }
                                
                                setSelectedReferral({
                                  ...selectedReferral,
                                  images: [...(selectedReferral.images || []), ...uploadedImages]
                                });
                                
                                toast({
                                  title: "Images added",
                                  description: `${uploadedImages.length} image(s) added. Remember to Save Changes.`
                                });
                              } catch (error) {
                                toast({
                                  title: "Upload failed",
                                  description: "Failed to process images",
                                  variant: "destructive"
                                });
                              } finally {
                                setUploadingImages(false);
                                e.target.value = '';
                              }
                            }}
                            disabled={uploadingImages || (selectedReferral.images?.length || 0) >= 5}
                          />
                        </label>
                      </div>
                    </div>
                    {uploadingImages && (
                      <div className="mt-2 text-center">
                        <p className="text-sm text-blue-600">Uploading images...</p>
                      </div>
                    )}
                  </div>
                )}
                
                {!isEditing && (
                  <p className="text-xs text-gray-500 mt-3 ml-3 italic">Click on any image to view full size</p>
                )}
                {isEditing && (
                  <p className="text-xs text-yellow-700 mt-3 ml-3 italic">Click X to remove an image. New images will be saved when you click "Save Changes".</p>
                )}
              </div>

              {/* Footer Disclaimer */}
              <div className="text-center border-t pt-4 mt-6">
                <p className="text-xs text-gray-500 italic">
                  ─────────────────────────────────────────────────
                </p>
                <p className="text-xs text-gray-600 italic mt-2">
                  This referral contains personal medical information protected by privacy laws.
                </p>
                <p className="text-xs text-gray-600 italic">
                  Please handle with appropriate confidentiality and security measures.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-6 mt-6 border-t">
                {isEditing ? (
                  <div className="w-full">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex space-x-2">
                        <Button onClick={handleUpdateReferral} className="bg-blue-600 hover:bg-blue-700">
                          Save Changes
                        </Button>
                        <Button variant="outline" onClick={() => {
                          // Reload the referral to discard changes
                          handleViewReferral(selectedReferral.id);
                        }}>
                          Cancel
                        </Button>
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          onClick={async () => {
                            try {
                              // First, save the changes
                              const response = await fetch(`${backendUrl}/api/referrals/${selectedReferral.id}`, {
                                method: 'PUT',
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                  chiefConcern: selectedReferral.chiefConcern,
                                  requestedService: selectedReferral.requestedService,
                                  toothArea: selectedReferral.toothArea,
                                  urgency: selectedReferral.urgency,
                                  medicalAlerts: selectedReferral.medicalAlerts,
                                  notesToSpecialist: selectedReferral.notesToSpecialist,
                                  images: selectedReferral.images || []
                                })
                              });

                              if (!response.ok) throw new Error('Failed to save');

                              toast({ title: "Success", description: "Changes saved" });
                              
                              // Reload referrals
                              await loadReferrals();
                              
                              // Exit edit mode
                              setIsEditing(false);
                              
                              // Open email modal
                              setEmailModalReferralId(selectedReferral.id);
                              setSendToSpecialist(true);
                              setSendToPatient(false);
                              setShowEmailModal(true);
                            } catch (error) {
                              toast({ title: "Error", description: "Failed to save changes", variant: "destructive" });
                            }
                          }}
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Save & Email
                        </Button>
                      </div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                      <p className="text-sm text-yellow-800 text-center font-bold">
                        "Save & Email" will save your changes and then send the referral to the specialist.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between w-full">
                    <div className="flex flex-wrap gap-2">
                      <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => handleDownloadReferral(selectedReferral.id)}>
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download
                      </Button>
                      <Button variant="outline" onClick={() => handlePrintReferral(selectedReferral.id)}>
                        <Printer className="h-4 w-4 mr-2" />
                        Print
                      </Button>
                      <Button variant="outline" onClick={() => handleEmailReferral(selectedReferral.id)}>
                        <Mail className="h-4 w-4 mr-2" />
                        Email
                      </Button>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={() => confirmDelete(selectedReferral.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Referral
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Referral?</h3>
                <p className="text-gray-600 mb-4">
                  Are you sure you want to delete this referral? This action cannot be undone and the referral will be permanently removed.
                </p>
                <div className="flex gap-3 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setReferralToDelete(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteReferral}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Yes, Delete
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ask DAN Chat Widget */}
      {showAskDan && (
        <div className="fixed bottom-20 right-6 w-96 bg-white rounded-lg shadow-xl z-50">
          <div className="bg-blue-600 text-white p-4 rounded-t-lg flex justify-between items-center">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_dental-dashboard-3/artifacts/a73bkdcb_a-cartoon-style-illustration-of-a-friend_BHWWQ3RORgyazFo06PAsDw_NyHovGrkR5m-Yi-_Gwdm5Q.png"
                alt="Dr. G"
                className="w-10 h-10 rounded-full mr-2 bg-white"
              />
              <span className="font-semibold">Ask Dr. G</span>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => {
                  setDanMessages([]);
                  setDanInput('');
                  toast({ title: "Chat cleared", description: "Starting fresh conversation" });
                }}
                className="hover:bg-blue-700 p-1 rounded"
                title="Clear chat"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button onClick={() => setShowAskDan(false)}><X className="h-5 w-5" /></button>
            </div>
          </div>
          <div className="h-96 overflow-y-auto p-4 space-y-4">
            {danMessages.length === 0 && (
              <div className="text-center py-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_dental-dashboard-3/artifacts/a73bkdcb_a-cartoon-style-illustration-of-a-friend_BHWWQ3RORgyazFo06PAsDw_NyHovGrkR5m-Yi-_Gwdm5Q.png"
                  alt="Dr. G"
                  className="w-24 h-24 mx-auto mb-4 rounded-full"
                />
                <p className="text-gray-700 font-medium mb-2">Need help finding a referral dentist?</p>
                <p className="text-gray-600 text-sm">That&apos;s where we come in. Type in the name, location, and specialty.</p>
              </div>
            )}
            {danMessages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded ${m.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {danLoading && <div className="text-center text-gray-500">Dr. G is thinking...</div>}
          </div>
          <div className="p-4 border-t">
            <div className="flex space-x-2">
              <Input
                placeholder="Ask Dr. G..."
                value={danInput}
                onChange={(e) => setDanInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAskDan()}
              />
              <Button onClick={handleAskDan} disabled={danLoading}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Email Recipient Selection Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Email Referral</DialogTitle>
            <DialogDescription>
              Select who should receive this referral email
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {(() => {
              const referral = referrals.find(r => r.id === emailModalReferralId);
              const patient = patients.find(p => p.id === referral?.patientId);
              const source = referralSources.find(s => s.id === referral?.referralSourceId);
              
              return (
                <>
                  {/* Specialist Checkbox */}
                  <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <Checkbox 
                      id="sendToSpecialist"
                      checked={sendToSpecialist}
                      onCheckedChange={setSendToSpecialist}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <label 
                        htmlFor="sendToSpecialist" 
                        className="text-sm font-medium leading-none cursor-pointer flex items-center gap-2"
                      >
                        <Stethoscope className="h-4 w-4 text-blue-600" />
                        Send to Specialist
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        {source?.name || 'Specialist'}
                      </p>
                      {source?.email ? (
                        <p className="text-xs text-gray-500 mt-1 truncate">
                          {source.email}
                        </p>
                      ) : (
                        <p className="text-xs text-red-500 mt-1">
                          ⚠️ No email address on file
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Patient Checkbox */}
                  <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <Checkbox 
                      id="sendToPatient"
                      checked={sendToPatient}
                      onCheckedChange={setSendToPatient}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <label 
                        htmlFor="sendToPatient" 
                        className="text-sm font-medium leading-none cursor-pointer flex items-center gap-2"
                      >
                        <User className="h-4 w-4 text-green-600" />
                        Send to Patient
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        {patient ? `${patient.firstName} ${patient.lastName}` : 'Patient'}
                      </p>
                      {patient?.email ? (
                        <p className="text-xs text-gray-500 mt-1 truncate">
                          {patient.email}
                        </p>
                      ) : (
                        <p className="text-xs text-red-500 mt-1">
                          ⚠️ No email address on file
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Validation Message */}
                  {!sendToSpecialist && !sendToPatient && (
                    <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                      ⚠️ Please select at least one recipient
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <DialogFooter className="sm:justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowEmailModal(false)}
              disabled={emailingReferral}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleEmailReferral}
              disabled={emailingReferral || (!sendToSpecialist && !sendToPatient)}
            >
              {emailingReferral ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SMS Modal */}
      <Dialog open={showSmsModal} onOpenChange={setShowSmsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-green-600" />
              Text Referral
            </DialogTitle>
            <DialogDescription>
              Send referral for {smsReferral?.patientName} via text message
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600 font-medium">Send to:</p>
            
            <div className="space-y-3">
              <div 
                onClick={() => setSmsRecipient('patient')}
                className={`p-3 border rounded-lg cursor-pointer transition ${
                  smsRecipient === 'patient' ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-600" />
                    <span className="font-medium">Patient Only</span>
                  </div>
                  {smsRecipient === 'patient' && <Check className="h-5 w-5 text-green-600" />}
                </div>
                <p className="text-xs text-gray-500 mt-1">{smsReferral?.patientName}</p>
              </div>

              <div 
                onClick={() => setSmsRecipient('dentist')}
                className={`p-3 border rounded-lg cursor-pointer transition ${
                  smsRecipient === 'dentist' ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Stethoscope className="h-4 w-4 text-gray-600" />
                    <span className="font-medium">Specialist Only</span>
                  </div>
                  {smsRecipient === 'dentist' && <Check className="h-5 w-5 text-green-600" />}
                </div>
                <p className="text-xs text-gray-500 mt-1">{smsReferral?.specialistName || smsReferral?.referralSourceName}</p>
              </div>

              <div 
                onClick={() => setSmsRecipient('both')}
                className={`p-3 border rounded-lg cursor-pointer transition ${
                  smsRecipient === 'both' ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-gray-600" />
                    <span className="font-medium">Both Patient & Specialist</span>
                  </div>
                  {smsRecipient === 'both' && <Check className="h-5 w-5 text-green-600" />}
                </div>
                <p className="text-xs text-gray-500 mt-1">Send to both recipients</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSmsModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendReferralSMS} 
              disabled={sendingSMS}
              className="bg-green-600 hover:bg-green-700"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              {sendingSMS ? 'Sending...' : 'Send Text'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== INBOUND REFERRALS MODALS ========== */}

      {/* Add Inbound Source Modal */}
      <Dialog open={showAddInboundSourceModal} onOpenChange={setShowAddInboundSourceModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Referral Source</DialogTitle>
            <DialogDescription>
              Add a person or entity who refers patients to your practice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-1">Referral Type *</label>
              <Select value={inboundFormData.sourceType} onValueChange={(v) => setInboundFormData({...inboundFormData, sourceType: v})}>
                <SelectTrigger data-testid="inbound-source-type-select">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {REFERRAL_SOURCE_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Name {(inboundFormData.sourceType === 'Online/Website' || inboundFormData.sourceType === 'Other') 
                  ? <span className="text-gray-400 font-normal">(optional - will use type as name)</span>
                  : '*'}
              </label>
              <Input
                value={inboundFormData.name}
                onChange={(e) => setInboundFormData({...inboundFormData, name: e.target.value})}
                placeholder={
                  inboundFormData.sourceType === 'Online/Website' ? 'e.g., Google, Yelp (leave blank for "Online")' :
                  inboundFormData.sourceType === 'Other' ? 'e.g., Community Event (leave blank for "Other")' :
                  'e.g., Dr. John Smith, Jane Doe'
                }
                data-testid="inbound-source-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input
                type="email"
                value={inboundFormData.email}
                onChange={(e) => setInboundFormData({...inboundFormData, email: e.target.value})}
                placeholder="email@example.com"
                data-testid="inbound-source-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <Input
                value={inboundFormData.phone}
                onChange={(e) => setInboundFormData({...inboundFormData, phone: e.target.value})}
                placeholder="(555) 123-4567"
                data-testid="inbound-source-phone-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Address</label>
              <Input
                value={inboundFormData.address}
                onChange={(e) => setInboundFormData({...inboundFormData, address: e.target.value})}
                placeholder="123 Main St, City, State"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={inboundFormData.notes}
                onChange={(e) => setInboundFormData({...inboundFormData, notes: e.target.value})}
                placeholder="Any additional notes..."
                className="w-full p-2 border rounded-md text-sm"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddInboundSourceModal(false); resetInboundForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleAddInboundSource} disabled={savingInboundSource} data-testid="save-inbound-source-btn">
              {savingInboundSource ? 'Saving...' : 'Add Source'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Inbound Source Modal */}
      <Dialog open={showEditInboundSourceModal} onOpenChange={setShowEditInboundSourceModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Referral Source</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <Input
                value={inboundFormData.name}
                onChange={(e) => setInboundFormData({...inboundFormData, name: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Referral Type *</label>
              <Select value={inboundFormData.sourceType} onValueChange={(v) => setInboundFormData({...inboundFormData, sourceType: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {REFERRAL_SOURCE_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input
                type="email"
                value={inboundFormData.email}
                onChange={(e) => setInboundFormData({...inboundFormData, email: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <Input
                value={inboundFormData.phone}
                onChange={(e) => setInboundFormData({...inboundFormData, phone: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Address</label>
              <Input
                value={inboundFormData.address}
                onChange={(e) => setInboundFormData({...inboundFormData, address: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={inboundFormData.notes}
                onChange={(e) => setInboundFormData({...inboundFormData, notes: e.target.value})}
                className="w-full p-2 border rounded-md text-sm"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditInboundSourceModal(false); resetInboundForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateInboundSource} disabled={savingInboundSource}>
              {savingInboundSource ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Inbound Source Details Modal */}
      <Dialog open={showViewInboundSourceModal} onOpenChange={setShowViewInboundSourceModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center">
                {selectedInboundSource && getInboundTypeIcon(selectedInboundSource.sourceType)}
                <span className="ml-2">{selectedInboundSource?.name}</span>
              </DialogTitle>
              {selectedInboundSource && (
                <Badge className={getInboundTypeColor(selectedInboundSource.sourceType)}>
                  {selectedInboundSource.sourceType}
                </Badge>
              )}
            </div>
            <DialogDescription>
              View referral source details and referred patients
            </DialogDescription>
          </DialogHeader>
          
          {inboundSourceDetails && (
            <div className="space-y-6 py-4">
              {/* Contact Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold mb-3">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{inboundSourceDetails.source.email || 'No email'}</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{inboundSourceDetails.source.phone || 'No phone'}</span>
                  </div>
                  {inboundSourceDetails.source.address && (
                    <div className="flex items-center col-span-2">
                      <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{inboundSourceDetails.source.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-green-800 text-sm">Total Referrals</h3>
                      <p className="text-3xl font-bold text-green-600">{inboundSourceDetails.totalReferrals}</p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => openLinkPatientModal(selectedInboundSource)}
                      className="bg-white"
                      data-testid="add-referral-from-modal"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Referral
                    </Button>
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <div>
                    <h3 className="font-semibold text-purple-800 text-sm">Thank Yous Sent</h3>
                    <p className="text-3xl font-bold text-purple-600">{inboundSourceDetails.totalThankYousSent || 0}</p>
                  </div>
                  {inboundSourceDetails.currentTier && (
                    <span className="inline-block mt-2 text-xs bg-purple-100 text-purple-700 border border-purple-300 px-2 py-0.5 rounded-full" data-testid="referral-tier-badge">
                      {inboundSourceDetails.currentTier.label}
                    </span>
                  )}
                </div>
              </div>

              {/* Referred Patients with Actions */}
              <div>
                <h3 className="font-semibold mb-3">Patients Referred & Thank You Actions</h3>
                {inboundSourceDetails.referredPatients.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {inboundSourceDetails.referredPatients.map((patient, idx) => (
                      <div key={patient.id || idx} className="bg-gray-50 p-4 rounded-lg border">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-medium">{patient.firstName} {patient.lastName}</p>
                            {patient.email && <p className="text-xs text-gray-500">{patient.email}</p>}
                            {patient.phone && <p className="text-xs text-gray-500">{patient.phone}</p>}
                            <p className="text-xs text-gray-400 mt-1">
                              Referred: {patient.inboundReferralLinkedAt 
                                ? new Date(patient.inboundReferralLinkedAt).toLocaleDateString() 
                                : patient.createdAt 
                                  ? new Date(patient.createdAt).toLocaleDateString() 
                                  : 'N/A'}
                            </p>
                            {patient.inboundReferralNotes && (
                              <p className="text-xs text-blue-600 mt-1 italic">"{patient.inboundReferralNotes}"</p>
                            )}
                            {patient.thankYouSentOn && (
                              <div className="flex items-center gap-1 mt-1" data-testid={`thank-you-sent-status-${patient.id}`}>
                                <Check className="h-3 w-3 text-green-600" />
                                <span className="text-xs text-green-600 font-medium">
                                  Thank You Sent {new Date(patient.thankYouSentOn).toLocaleDateString()}
                                </span>
                                {patient.thankYouTier && (
                                  <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full ml-1">
                                    {patient.thankYouTier}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Thank You Letter Actions */}
                        <div className="grid grid-cols-5 gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openSendThankYouModal(selectedInboundSource.id, patient.id)}
                            className="text-blue-600 hover:bg-blue-50"
                            title="Email Thank You"
                            data-testid={`email-thank-you-${patient.id}`}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openSendThankYouModal(selectedInboundSource.id, patient.id)}
                            className="text-green-600 hover:bg-green-50"
                            title="Text Thank You"
                            data-testid={`text-thank-you-${patient.id}`}
                          >
                            <MessageSquare className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadThankYouPdf(selectedInboundSource.id, patient.id)}
                            title="Download PDF"
                            data-testid={`download-thank-you-${patient.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePrintThankYouPdf(selectedInboundSource.id, patient.id)}
                            title="Print"
                            data-testid={`print-thank-you-${patient.id}`}
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadThankYouPdf(selectedInboundSource.id, patient.id)}
                            title="View PDF"
                            data-testid={`view-thank-you-${patient.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No patients referred yet</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openLinkPatientModal(selectedInboundSource)}
                      className="mt-2"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Link First Patient
                    </Button>
                  </div>
                )}
              </div>

              {/* Notes */}
              {inboundSourceDetails.source.notes && (
                <div>
                  <h3 className="font-semibold mb-2">Notes</h3>
                  <p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-lg">
                    {inboundSourceDetails.source.notes}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => openDeleteInboundConfirm(selectedInboundSource)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowViewInboundSourceModal(false);
                openEditInboundSourceModal(selectedInboundSource);
              }}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button onClick={() => setShowViewInboundSourceModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Inbound Source Confirmation Modal */}
      <Dialog open={showDeleteInboundConfirm} onOpenChange={setShowDeleteInboundConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Referral Source</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedInboundSource?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteInboundConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteInboundSource}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Patient to Source Modal */}
      <Dialog open={showLinkPatientModal} onOpenChange={setShowLinkPatientModal}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <UserPlus className="h-5 w-5 mr-2 text-green-600" />
              Add Referral from {selectedInboundSource?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedInboundSource?.sourceType === 'Dentist' || selectedInboundSource?.sourceType === 'Patient' || selectedInboundSource?.sourceType === 'Friend/Family'
                ? 'A branded thank you email will be sent automatically.'
                : 'No automatic email for this source type. You can optionally send a text.'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Mode Toggle */}
          <div className="flex border rounded-lg p-1 bg-gray-50 gap-1">
            <button
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                addReferralMode === 'new' 
                  ? 'bg-white shadow text-green-700 border border-green-200' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              onClick={() => { setAddReferralMode('new'); setDuplicatePatient(null); }}
              data-testid="add-referral-new-tab"
            >
              <Plus className="h-4 w-4 inline mr-1" />
              New Patient
            </button>
            <button
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                addReferralMode === 'existing' 
                  ? 'bg-white shadow text-blue-700 border border-blue-200' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              onClick={() => { setAddReferralMode('existing'); setDuplicatePatient(null); }}
              data-testid="add-referral-existing-tab"
            >
              <Search className="h-4 w-4 inline mr-1" />
              Existing Patient
            </button>
          </div>

          {/* Duplicate Patient Warning */}
          {duplicatePatient && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-800">Duplicate Patient Found</p>
                  <p className="text-sm text-amber-700 mt-1">
                    {duplicatePatient.firstName} {duplicatePatient.lastName}
                    {duplicatePatient.email && <span className="ml-2">({duplicatePatient.email})</span>}
                    {duplicatePatient.phone && <span className="ml-2">{duplicatePatient.phone}</span>}
                  </p>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="mt-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={handleLinkDuplicatePatient}
                  >
                    Link This Patient Instead
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4 py-2">
            {/* NEW PATIENT MODE */}
            {addReferralMode === 'new' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">First Name *</label>
                    <Input
                      value={newReferralPatient.firstName}
                      onChange={(e) => setNewReferralPatient({...newReferralPatient, firstName: e.target.value})}
                      placeholder="John"
                      data-testid="new-referral-firstname"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Last Name *</label>
                    <Input
                      value={newReferralPatient.lastName}
                      onChange={(e) => setNewReferralPatient({...newReferralPatient, lastName: e.target.value})}
                      placeholder="Smith"
                      data-testid="new-referral-lastname"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <Input
                      type="email"
                      value={newReferralPatient.email}
                      onChange={(e) => setNewReferralPatient({...newReferralPatient, email: e.target.value})}
                      placeholder="patient@email.com"
                      data-testid="new-referral-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone</label>
                    <Input
                      type="tel"
                      value={newReferralPatient.phone}
                      onChange={(e) => setNewReferralPatient({...newReferralPatient, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                      data-testid="new-referral-phone"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Notes about this referral</label>
                  <textarea
                    value={newReferralPatient.notes}
                    onChange={(e) => setNewReferralPatient({...newReferralPatient, notes: e.target.value})}
                    placeholder="Any notes about this referral..."
                    className="w-full p-3 border rounded-md text-sm"
                    rows={2}
                    data-testid="new-referral-notes"
                  />
                </div>
                
                {/* Thank You Info Box */}
                <div className={`p-4 rounded-lg border ${
                  selectedInboundSource?.sourceType === 'Dentist' || selectedInboundSource?.sourceType === 'Patient'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-start">
                    <Mail className={`h-5 w-5 mt-0.5 mr-3 ${
                      selectedInboundSource?.sourceType === 'Dentist' || selectedInboundSource?.sourceType === 'Patient'
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }`} />
                    <div className="flex-1">
                      {selectedInboundSource?.sourceType === 'Dentist' || selectedInboundSource?.sourceType === 'Patient' ? (
                        <>
                          <p className="font-medium text-green-800">Thank You Email</p>
                          <p className="text-sm text-green-700">
                            {selectedInboundSource?.email 
                              ? `Will be sent automatically to ${selectedInboundSource.email}`
                              : 'No email on file - please add email to send thank you'}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="font-medium text-gray-600">No Auto Email</p>
                          <p className="text-sm text-gray-500">
                            Emails are not sent automatically for "{selectedInboundSource?.sourceType}" source type
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Optional Text */}
                  {selectedInboundSource?.phone && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <label className="flex items-center text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={addReferralSendText}
                          onChange={(e) => setAddReferralSendText(e.target.checked)}
                          className="mr-2 h-4 w-4"
                        />
                        <MessageSquare className="h-4 w-4 mr-1 text-blue-500" />
                        Also send a text to {selectedInboundSource.phone}
                      </label>
                    </div>
                  )}
                </div>
              </>
            )}
            
            {/* EXISTING PATIENT MODE */}
            {addReferralMode === 'existing' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Select Patient *</label>
                  <Select value={linkPatientId} onValueChange={setLinkPatientId}>
                    <SelectTrigger data-testid="link-patient-select">
                      <SelectValue placeholder="Search and select patient..." />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map((patient) => (
                        <SelectItem key={patient.id} value={patient.id}>
                          {patient.firstName} {patient.lastName}
                          {patient.email && <span className="text-gray-500 ml-2">({patient.email})</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Referral Notes (Optional)</label>
                  <textarea
                    value={linkPatientNotes}
                    onChange={(e) => setLinkPatientNotes(e.target.value)}
                    placeholder="Any notes about this referral..."
                    className="w-full p-3 border rounded-md text-sm"
                    rows={2}
                    data-testid="link-patient-notes"
                  />
                </div>
                
                {/* Thank You Options for existing patient */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between mb-3">
                    <label className="font-medium text-green-800">Send Thank You?</label>
                    <input
                      type="checkbox"
                      checked={linkSendThankYou}
                      onChange={(e) => setLinkSendThankYou(e.target.checked)}
                      className="h-4 w-4 text-green-600"
                      data-testid="link-send-thank-you"
                    />
                  </div>
                  
                  {linkSendThankYou && (
                    <div className="space-y-2 ml-4">
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={linkSendEmail}
                          onChange={(e) => setLinkSendEmail(e.target.checked)}
                          className="mr-2 h-4 w-4"
                        />
                        <Mail className="h-4 w-4 mr-1 text-blue-500" />
                        Send Email
                        {selectedInboundSource?.email && <span className="text-gray-500 ml-1">({selectedInboundSource.email})</span>}
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={linkSendSms}
                          onChange={(e) => setLinkSendSms(e.target.checked)}
                          className="mr-2 h-4 w-4"
                        />
                        <MessageSquare className="h-4 w-4 mr-1 text-green-500" />
                        Send Text
                        {selectedInboundSource?.phone && <span className="text-gray-500 ml-1">({selectedInboundSource.phone})</span>}
                      </label>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkPatientModal(false)}>
              Cancel
            </Button>
            {addReferralMode === 'new' ? (
              <Button 
                onClick={handleAddNewPatientReferral} 
                disabled={addingReferral || !newReferralPatient.firstName.trim() || !newReferralPatient.lastName.trim()} 
                data-testid="add-new-patient-submit"
                className="bg-green-600 hover:bg-green-700"
              >
                {addingReferral ? 'Adding Patient...' : 'Add Patient & Send Thank You'}
              </Button>
            ) : (
              <Button 
                onClick={handleLinkPatient} 
                disabled={linkingPatient || !linkPatientId} 
                data-testid="link-patient-submit"
              >
                {linkingPatient ? 'Linking...' : 'Link Patient'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Thank You Modal */}
      <Dialog open={showSendThankYouModal} onOpenChange={setShowSendThankYouModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Heart className="h-5 w-5 mr-2 text-pink-500" />
              Send Thank You Letter
            </DialogTitle>
            <DialogDescription>
              Send a thank you letter to the referral source. You can customize the message.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Send Options */}
            <div className="space-y-3">
              <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={thankYouSendEmail}
                  onChange={(e) => setThankYouSendEmail(e.target.checked)}
                  className="mr-3 h-4 w-4"
                />
                <Mail className="h-5 w-5 mr-2 text-blue-500" />
                <span>Send via Email</span>
              </label>
              <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={thankYouSendSms}
                  onChange={(e) => setThankYouSendSms(e.target.checked)}
                  className="mr-3 h-4 w-4"
                />
                <MessageSquare className="h-5 w-5 mr-2 text-green-500" />
                <span>Send via Text</span>
              </label>
            </div>
            
            {/* Custom Message */}
            <div>
              <label className="block text-sm font-medium mb-2">Custom Message (Optional)</label>
              <textarea
                value={thankYouCustomMessage}
                onChange={(e) => setThankYouCustomMessage(e.target.value)}
                placeholder="Leave blank to use the default template, or write a custom message..."
                className="w-full p-3 border rounded-md text-sm"
                rows={4}
                data-testid="thank-you-custom-message"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use {'{patient_name}'} and {'{practice_name}'} as placeholders.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendThankYouModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendCustomThankYou} 
              disabled={sendingThankYou || (!thankYouSendEmail && !thankYouSendSms)}
              data-testid="send-thank-you-submit"
            >
              {sendingThankYou ? 'Sending...' : 'Send Thank You'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Editor Modal */}
      <Dialog open={showThankYouTemplateModal} onOpenChange={setShowThankYouTemplateModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Settings className="h-5 w-5 mr-2" />
              Thank You Letter Templates
            </DialogTitle>
            <DialogDescription>
              Customize the thank you letter templates for patient and dentist referrals.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {thankYouTemplates.map((template, idx) => (
              <div key={template.templateType || idx} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold capitalize flex items-center">
                    {template.templateType === 'dentist' ? (
                      <Stethoscope className="h-4 w-4 mr-2 text-blue-500" />
                    ) : (
                      <Users className="h-4 w-4 mr-2 text-green-500" />
                    )}
                    {template.templateType} Referral Template
                  </h3>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingTemplate({...template})}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                </div>
                
                <div className="text-sm text-gray-600 space-y-2">
                  <p><strong>Subject:</strong> {template.subject}</p>
                  <p><strong>Greeting:</strong> {template.greeting}</p>
                  <p className="line-clamp-2"><strong>Body:</strong> {template.bodyText?.substring(0, 100)}...</p>
                </div>
              </div>
            ))}
            
            {/* Template Edit Form */}
            {editingTemplate && (
              <div className="border-t pt-6 mt-4">
                <h3 className="font-semibold mb-4">
                  Editing: {editingTemplate.templateType} Referral Template
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Subject Line</label>
                    <Input
                      value={editingTemplate.subject || ''}
                      onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                      placeholder="Thank You for Your Referral!"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Greeting</label>
                    <Input
                      value={editingTemplate.greeting || ''}
                      onChange={(e) => setEditingTemplate({...editingTemplate, greeting: e.target.value})}
                      placeholder="Dear {source_name},"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Body Text</label>
                    <textarea
                      value={editingTemplate.bodyText || ''}
                      onChange={(e) => setEditingTemplate({...editingTemplate, bodyText: e.target.value})}
                      className="w-full p-3 border rounded-md text-sm"
                      rows={5}
                      placeholder="Thank you for referring {patient_name}..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Closing Text</label>
                    <textarea
                      value={editingTemplate.closingText || ''}
                      onChange={(e) => setEditingTemplate({...editingTemplate, closingText: e.target.value})}
                      className="w-full p-3 border rounded-md text-sm"
                      rows={2}
                      placeholder="With gratitude,&#10;The Team at {practice_name}"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Available placeholders: {'{source_name}'}, {'{patient_name}'}, {'{practice_name}'}
                  </p>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveTemplate} disabled={savingTemplate}>
                      {savingTemplate ? 'Saving...' : 'Save Template'}
                    </Button>
                    <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={() => { setShowThankYouTemplateModal(false); setEditingTemplate(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReferralManagementPage;
